/**
 * Route Builder Component
 * 
 * Material-UI adaptation of CirculatorWebpage RouteBuilder.
 * Manages fixed routes (including circulators).
 */

import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Paper,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Menu,
  Snackbar,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import { logEvent } from '../../services/analytics';
import { searchAddress, type GeocodeResult } from '../../services/geocoding';
import { buildReversedRoute } from '../../utils/fixedRoutes/returnRoute';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Checkbox from '@mui/material/Checkbox';
import RepeatIcon from '@mui/icons-material/Repeat';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import CheckIcon from '@mui/icons-material/Check';
import Tooltip from '@mui/material/Tooltip';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SensorsIcon from '@mui/icons-material/Sensors';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AltRouteOutlined from '@mui/icons-material/AltRouteOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import DirectionsBusOutlined from '@mui/icons-material/DirectionsBusOutlined';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';
import { describeServiceDays } from './attachOperations/draftPlan';
import { useFixedRoutes } from '../../context/FixedRoutesContext';
import { useRouteOverlap } from '../../context/RouteOverlapContext';
import { AppContext, type Layer, type Point } from '../../context/context';
import type { FixedRoute, FixedRouteStopConfig, FixedRouteSegment, FixedRouteStop, ServiceDaysMask } from '../../types/fixedRoutes';
import { DEFAULT_TRAFFIC_MULTIPLIER, DEFAULT_OPERATING_HOURS, MIN_TRAFFIC_MULTIPLIER, MAX_TRAFFIC_MULTIPLIER } from '../../config/constants';
import { StopSequenceEditor } from './StopSequenceEditor';
import { exportRouteToExcel, exportRouteToKML } from '../../utils/fixedRoutes/exportHelpers';
import { calculateTravelTimeAndDistance as osrmCalculateTravelTimeAndDistance, calculateTravelTimesAndDistancesBatch as osrmCalculateTravelTimesAndDistancesBatch } from '../../services/routing/osrmClient';
import { calculateTravelTimeAndDistance as hereCalculateTravelTimeAndDistance, calculateTravelTimesAndDistancesBatch as hereCalculateTravelTimesAndDistancesBatch } from '../../services/routing/hereClient';
import { generateRouteColor } from '../../utils/fixedRoutes/routeToLineConverter';
import { formatDistance } from '../../utils/fixedRoutes/distanceFormatter';
import { formatCurrency, formatCurrencyCompact } from '../../utils/fixedRoutes/currencyFormatter';
import { computeRouteCost } from '../../services/fixedRoutes/fixedRouteCostCalculator';
import { calculateRouteLoopDuration } from '../../utils/fixedRoutes/vehicleCalculator';
import { calculateHeadway, calculateFrequency } from '../../utils/fixedRoutes/headwayFrequencyCalculator';
import { getDistanceMeters } from '../map/utils/distance';
import { OpenStreetMapService } from '../../services/osm/OpenStreetMapService';
import { getAtlasApiBaseUrl } from '../../services/getAtlasApiBaseUrl';
import { atlasFetch } from '../../services/api/atlasBackendClient';
import RouteOverlapAnalysisModal from './RouteOverlapAnalysisModal';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
const DEFAULT_ROUTE_START_TIME = '08:00';
const DEFAULT_WAIT_TIME_MINUTES = 2;
const CAPTURE_FINISH_DISTANCE_METERS = 20;
const CAPTURE_EXISTING_STOP_SNAP_DISTANCE_METERS = 35;
const CAPTURE_DISABLE_NEAR_STOP_FINISH_FLAG = '__FIXED_ROUTE_CAPTURE_DISABLE_NEAR_STOP_FINISH__';
const CAPTURE_DRAFT_PERSIST_DEBOUNCE_MS = 80;
const CAPTURE_SEGMENT_RENDER_TIMEOUT_MS = 8000;

const CAPTURE_PREPROCESS_CONCURRENCY = 3;
const getStopSequenceKey = (configs: FixedRouteStopConfig[]): string =>
  configs.map((s) => s.stopId).join('|');

const fixedRouteCaptureOsmService = new OpenStreetMapService({
  baseUrl: 'https://overpass-api.de',
  rateLimit: {
    requestsPerMinute: 30,
    requestsPerHour: 500,
  },
  cache: {
    ttl: 3600000,
    maxSize: 100,
  },
  timeout: 30000,
});

// Allow more than the backend's per-point OSRM nearest budget (~4s) so a healthy snap isn't
// aborted mid-flight (which would needlessly trip the circuit breaker). A truly unreachable OSRM
// fails fast on the backend and returns the original point, so this ceiling only bites on hangs.
const SNAP_TIMEOUT_MS = 6000;
const SNAP_CIRCUIT_COOLDOWN_MS = 30000;
// Circuit breaker for the ORS snap backend. Snapping is best-effort, but the snap endpoint can
// hang (ORS unavailable), and because it shares the routing backend, hung snaps STARVE the OSRM
// route/travel-time calls — so routes stop being produced. Once a snap times out/fails we stop
// snapping for a cooldown (skipping straight to the clicked point) so OSRM keeps flowing, then retry.
let snapCircuitOpenUntil = 0;

async function snapPointToRoad(point: [number, number]): Promise<[number, number]> {
  if (Date.now() < snapCircuitOpenUntil) {
    return point; // snap recently failed — don't fire more hung requests that starve routing
  }
  const apiBase = getAtlasApiBaseUrl();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), SNAP_TIMEOUT_MS);
  let response: Response;
  try {
    response = await atlasFetch(`${apiBase}/api/openroute/snap-points`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        points: [point],
      }),
      signal: controller.signal,
    });
  } catch (error) {
    snapCircuitOpenUntil = Date.now() + SNAP_CIRCUIT_COOLDOWN_MS; // open circuit on timeout/network error
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!response.ok) {
    snapCircuitOpenUntil = Date.now() + SNAP_CIRCUIT_COOLDOWN_MS;
    throw new Error(`Failed to snap point to road (${response.status})`);
  }

  const data = await response.json();
  const snapped = data?.snapped_points?.[0];
  if (
    !snapped ||
    !Array.isArray(snapped) ||
    snapped.length < 2 ||
    typeof snapped[0] !== 'number' ||
    typeof snapped[1] !== 'number'
  ) {
    return point;
  }

  return [snapped[0], snapped[1]];
}

async function getOsmStopName(point: [number, number]): Promise<string> {
  return fixedRouteCaptureOsmService.getAddressForCoordinates(point);
}

type CaptureEventType = 'stop' | 'waypoint';

type QueuedCaptureEvent = {
  seq: number;
  sessionId: number;
  type: CaptureEventType;
  lat: number;
  lng: number;
};

type PreparedCaptureEvent = {
  seq: number;
  sessionId: number;
  type: CaptureEventType;
  snappedPoint: [number, number];
  rawPoint?: [number, number];
  stopName?: string;
};

type DraftUpsertArgs = {
  nextStops: FixedRouteStopConfig[];
  nextWaypoints: [number, number][];
  nextWaypointStopIndices: number[];
  nextSegments?: FixedRouteSegment[];
  options?: {
    isCirculatorOverride?: boolean;
    endTimeOverride?: string;
    reverseReturnJourneyOverride?: boolean;
  };
};

function hasValidStopCoordinates(stop: FixedRouteStop | undefined | null): stop is FixedRouteStop {
  if (!stop?.coordinates) return false;
  const { lat, lng } = stop.coordinates;
  return Number.isFinite(lat) && Number.isFinite(lng);
}

function findNearestExistingStop(
  clickPoint: [number, number],
  allStops: FixedRouteStop[],
  maxDistanceMeters: number
): FixedRouteStop | null {
  let nearest: FixedRouteStop | null = null;
  let nearestDistance = Number.POSITIVE_INFINITY;

  for (const stop of allStops) {
    if (!hasValidStopCoordinates(stop)) continue;
    const stopPoint: [number, number] = [stop.coordinates.lat, stop.coordinates.lng];
    const distance = getDistanceMeters(clickPoint, stopPoint);
    if (distance <= maxDistanceMeters && distance < nearestDistance) {
      nearest = stop;
      nearestDistance = distance;
    }
  }

  return nearest;
}

function findNearestExistingRouteStop(
  clickPoint: [number, number],
  routeStopConfigs: FixedRouteStopConfig[],
  allStops: FixedRouteStop[],
  maxDistanceMeters: number
): FixedRouteStop | null {
  const routeStopIdSet = new Set(routeStopConfigs.map((stopConfig) => stopConfig.stopId));
  const routeStops = allStops.filter((stop) => routeStopIdSet.has(stop.id));
  return findNearestExistingStop(clickPoint, routeStops, maxDistanceMeters);
}

function projectPointOntoSegment(
  point: [number, number],
  segmentStart: [number, number],
  segmentEnd: [number, number]
): { projection: [number, number]; t: number } {
  const ax = segmentStart[0];
  const ay = segmentStart[1];
  const bx = segmentEnd[0];
  const by = segmentEnd[1];
  const px = point[0];
  const py = point[1];

  const abx = bx - ax;
  const aby = by - ay;
  const abLenSq = abx * abx + aby * aby;

  if (abLenSq === 0) {
    return { projection: segmentStart, t: 0 };
  }

  const apx = px - ax;
  const apy = py - ay;
  const unclampedT = (apx * abx + apy * aby) / abLenSq;
  const t = Math.max(0, Math.min(1, unclampedT));
  return {
    projection: [ax + abx * t, ay + aby * t],
    t,
  };
}

function resolveStopForRouteOrdering(
  stopId: string,
  allStops: FixedRouteStop[],
  targetStop: FixedRouteStop
): FixedRouteStop | null {
  if (stopId === targetStop.id) return targetStop;
  const found = allStops.find((stop) => stop.id === stopId);
  return hasValidStopCoordinates(found) ? found : null;
}

// Only treat a captured click as a mid-route insertion when it lands essentially ON an existing
// inner segment. Otherwise append. Previously this always inserted at the *nearest* segment, which
// reordered stops while a route was being built sequentially (a click that happened to project near
// an earlier segment jumped into the middle).
const CAPTURE_INSERT_ON_SEGMENT_THRESHOLD_METERS = 25;

function findInsertionIndexForCapturedStop(
  clickPoint: [number, number],
  currentStopConfigs: FixedRouteStopConfig[],
  allStops: FixedRouteStop[],
  targetStop: FixedRouteStop
): number {
  const appendIndex = currentStopConfigs.length;
  if (currentStopConfigs.length < 2) return appendIndex;

  let bestInsertIndex = appendIndex;
  let bestDistanceMeters = Number.POSITIVE_INFINITY;

  for (let i = 0; i < currentStopConfigs.length - 1; i += 1) {
    const fromStop = resolveStopForRouteOrdering(currentStopConfigs[i].stopId, allStops, targetStop);
    const toStop = resolveStopForRouteOrdering(currentStopConfigs[i + 1].stopId, allStops, targetStop);
    if (!fromStop?.coordinates || !toStop?.coordinates) continue;

    const fromPoint: [number, number] = [fromStop.coordinates.lat, fromStop.coordinates.lng];
    const toPoint: [number, number] = [toStop.coordinates.lat, toStop.coordinates.lng];
    const { projection } = projectPointOntoSegment(clickPoint, fromPoint, toPoint);
    const distanceMeters = getDistanceMeters(clickPoint, projection);

    if (distanceMeters < bestDistanceMeters) {
      bestDistanceMeters = distanceMeters;
      bestInsertIndex = i + 1;
    }
  }

  // Append when building outward; only insert mid-route for a deliberate click on the line.
  return bestDistanceMeters <= CAPTURE_INSERT_ON_SEGMENT_THRESHOLD_METERS ? bestInsertIndex : appendIndex;
}

function dedupeStopConfigsByStopId(stopConfigs: FixedRouteStopConfig[]): FixedRouteStopConfig[] {
  const seenStopIds = new Set<string>();
  const deduped: FixedRouteStopConfig[] = [];
  for (const stopConfig of stopConfigs) {
    if (seenStopIds.has(stopConfig.stopId)) continue;
    seenStopIds.add(stopConfig.stopId);
    deduped.push(stopConfig);
  }
  return deduped;
}

/**
 * Format total time in minutes to a readable string (e.g., "2h 30min" or "45min")
 */
function formatTotalTime(totalMinutes: number): string {
  if (totalMinutes < 60) {
    return `${totalMinutes}min`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}min`;
}

/**
 * Convert time string to minutes since midnight
 */
function timeToMinutes(timeStr: string): number {
  const parts = timeStr.split(':');
  const hours = parseInt(parts[0] || '0', 10);
  const minutes = parseInt(parts[1] || '0', 10);
  return hours * 60 + minutes;
}

interface RouteBuilderProps {
  isDrawerOpen?: boolean;
}

export const RouteBuilder: React.FC<RouteBuilderProps> = ({ isDrawerOpen = false }) => {
  const { t } = useTranslation('fixedRoutes');
  const { routes, stops, addStop, updateStop, deleteStop, addRoute, insertRoute, updateRoute, deleteRoute, distanceUnit, setDistanceUnit, costSettings, selectedRouteId, setSelectedRouteId, trafficMultiplier: globalTrafficMultiplier, setRouteGenerationStatus, editingRouteId, enterEditMode, exitEditMode } = useFixedRoutes();
  const { openModal: openRouteOverlapModal } = useRouteOverlap();
  const appContext = React.useContext(AppContext);
  
  // Refs for measuring chip positions in optimization modal
  const originalChipRefs = useRef<Array<HTMLDivElement | null>>([]);
  const optimizedChipRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [selectedRoute, setSelectedRoute] = useState<FixedRoute | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState<FixedRoute | null>(null);
  const [orphanedStopsDecision, setOrphanedStopsDecision] = useState<{
    route: FixedRoute;
    orphanStopIds: string[];
  } | null>(null);
  const [showRecalculateDialog, setShowRecalculateDialog] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    route: FixedRoute;
  } | null>(null);
  const [isRouteCaptureMode, setIsRouteCaptureMode] = useState(false);
  const [capturePoints, setCapturePoints] = useState<Array<{ type: 'stop' | 'waypoint'; coordinates: [number, number] }>>([]);
  const [captureWaypoints, setCaptureWaypoints] = useState<[number, number][]>([]);
  const [captureWaypointStopIndices, setCaptureWaypointStopIndices] = useState<number[]>([]);
  const [isCaptureBusy, setIsCaptureBusy] = useState(false);
  const [draftRouteId, setDraftRouteId] = useState<string | null>(null);
  /** Same as draftRouteId but updated synchronously so rapid capture commits (same tick) reuse one route. */
  const draftRouteIdRef = useRef<string | null>(null);
  /** Last draft body written in upsertDraftRoute; context `routes` may lag one frame behind. */
  const draftRouteSnapshotRef = useRef<FixedRoute | null>(null);

  const clearDraftRouteTracking = useCallback(() => {
    draftRouteIdRef.current = null;
    draftRouteSnapshotRef.current = null;
    setDraftRouteId(null);
  }, []);

  // Form state
  const [routeName, setRouteName] = useState('');
  const [startTime, setStartTime] = useState(DEFAULT_ROUTE_START_TIME);
  const [endTime, setEndTime] = useState('');
  const [selectedStopId, setSelectedStopId] = useState('');
  const [addFromLayerSelection, setAddFromLayerSelection] = useState('');
  // Address/place search (F1): type-ahead geocoding to add a stop by address.
  const [addressInput, setAddressInput] = useState('');
  const [addressOptions, setAddressOptions] = useState<GeocodeResult[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const addressSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addressSearchAbortRef = useRef<AbortController | null>(null);
  const mapCentreRef = useRef(appContext?.mapSettings.mapCentre);
  mapCentreRef.current = appContext?.mapSettings.mapCentre;
  // Debounced + cancelable geocoding for the address search (research: debounce ~300ms + abort superseded).
  useEffect(() => {
    const query = addressInput.trim();
    if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);
    if (query.length < 3) {
      setAddressOptions([]);
      setAddressLoading(false);
      return;
    }
    setAddressLoading(true);
    addressSearchTimerRef.current = setTimeout(() => {
      addressSearchAbortRef.current?.abort();
      const controller = new AbortController();
      addressSearchAbortRef.current = controller;
      searchAddress(query, { signal: controller.signal, near: mapCentreRef.current })
        .then((results) => {
          if (!controller.signal.aborted) setAddressOptions(results);
        })
        .catch((error) => {
          if (!(error instanceof DOMException && error.name === 'AbortError')) setAddressOptions([]);
        })
        .finally(() => {
          if (!controller.signal.aborted) setAddressLoading(false);
        });
    }, 300);
    return () => {
      if (addressSearchTimerRef.current) clearTimeout(addressSearchTimerRef.current);
    };
  }, [addressInput]);
  const [stopConfigs, setStopConfigs] = useState<FixedRouteStopConfig[]>([]);
  const [trafficMultiplier, setTrafficMultiplier] = useState(DEFAULT_TRAFFIC_MULTIPLIER);
  const [segments, setSegments] = useState<FixedRouteSegment[]>([]);
  const [isCirculator, setIsCirculator] = useState(false);
  const [reverseReturnJourney, setReverseReturnJourney] = useState(true);
  const [isBusRoute, setIsBusRoute] = useState(false);
  // Freehand mode (F3): draw literal straight lines between stops, no road snapping/routing.
  const [freehand, setFreehand] = useState(false);
  const isFreehandRef = useRef(false);
  useEffect(() => { isFreehandRef.current = freehand; }, [freehand]);
  const [vehicleCount, setVehicleCount] = useState<number | undefined>(1); // Default to 1 vehicle
  const [vehicleCapacity, setVehicleCapacity] = useState<number | undefined>(50); // Default to 50
  const [isCalculating, setIsCalculating] = useState(false);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [calculationProgress, setCalculationProgress] = useState<string>('');
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [failedSegments, setFailedSegments] = useState<number[]>([]);
  const [manuallyEditedSegments, setManuallyEditedSegments] = useState<Set<string>>(new Set()); // Track segments manually edited by user
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const [routeSearchQuery, setRouteSearchQuery] = useState('');
  const [routeSortBy, setRouteSortBy] = useState<'name-asc' | 'name-desc' | 'start-time-asc' | 'start-time-desc' | 'stops-desc' | 'stops-asc' | 'type' | 'distance-desc' | 'distance-asc'>('name-asc');
  // When the drawer is dragged narrow, the route summary metrics wrap onto several lines and
  // waste vertical space. Below this container width we drop the descriptive words
  // (travel/total/stops/daily & hourly capacity/…) and keep just the icon + value. A callback
  // ref drives a ResizeObserver so this tracks the actual rendered width (drag, nudge, or
  // viewport resize) and re-attaches when the list mounts.
  const COMPACT_METRICS_WIDTH_PX = 460;
  const routeListObserverRef = useRef<ResizeObserver | null>(null);
  const [compactRouteMetrics, setCompactRouteMetrics] = useState(false);
  const routeListRef = useCallback((node: HTMLDivElement | null) => {
    if (routeListObserverRef.current) {
      routeListObserverRef.current.disconnect();
      routeListObserverRef.current = null;
    }
    if (node && typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect.width ?? node.clientWidth;
        setCompactRouteMetrics(width < COMPACT_METRICS_WIDTH_PX);
      });
      observer.observe(node);
      routeListObserverRef.current = observer;
    }
  }, []);
  const [editorTab, setEditorTab] = useState(0);
  // Per-route GTFS service calendar + headsign/description (Route Details tab).
  // `serviceDays` undefined ⇒ the GTFS export falls back to the export modal's
  // default calendar. Date range / exceptions are preserved from import but not
  // edited inline here.
  const [serviceDays, setServiceDays] = useState<ServiceDaysMask | undefined>(undefined);
  const [tripHeadsign, setTripHeadsign] = useState('');
  const [routeDesc, setRouteDesc] = useState('');
  // Per-stop wait-time inputs are hidden on the stop cards (set wait time globally via
  // Route Details → "Wait Time (All Stops)"). Data is preserved; only the per-card UI is removed.
  const waitInputsEnabled = false;
  
  // Optimization result modal state
  const [optimizationResult, setOptimizationResult] = useState<{
    originalDistance: number;
    optimizedDistance: number;
    distanceSaved: number;
    savingsPercent: string;
    originalOrder: string[];
    optimizedOrder: string[];
  } | null>(null);
  
  // Cache for distance/time calculations: key = "stopId1-stopId2", value = { distanceMeters, travelTimeMinutes }
  const distanceTimeCacheRef = useRef<Map<string, { distanceMeters: number; travelTimeMinutes: number }>>(new Map());
  const circulatorReturnLegCacheRef = useRef<{
    stopSequenceKey: string;
    segment: FixedRouteSegment;
    geometry?: FixedRoute['geometry'];
  } | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    routeName?: string;
    startTime?: string;
    endTime?: string;
    trafficMultiplier?: string;
    vehicleCount?: string;
  }>({});
  
  // Track if route name was manually entered by user
  const routeNameManuallySet = useRef(false);
  const lastAutoGeneratedName = useRef<string>('');

  // Undo/Redo history
  type RouteState = {
    routeName: string;
    startTime: string;
    endTime: string;
    stopConfigs: FixedRouteStopConfig[];
    segments: FixedRouteSegment[];
    trafficMultiplier: number;
    isCirculator: boolean;
    reverseReturnJourney: boolean;
    isBusRoute: boolean;
    freehand: boolean;
    vehicleCount?: number;
    vehicleCapacity?: number;
  };

  const [history, setHistory] = useState<RouteState[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoRef = useRef(false); // Flag to prevent adding to history during undo/redo

  // Save current state to history
  const saveToHistory = useCallback(() => {
    // Skip if we're in the middle of undo/redo or not editing
    if (isUndoRedoRef.current || !isEditing || isCreating) {
      return;
    }

    const currentState: RouteState = {
      routeName,
      startTime,
      endTime,
      stopConfigs: [...stopConfigs],
      segments: [...segments],
      trafficMultiplier,
      isCirculator,
      reverseReturnJourney,
      isBusRoute,
      freehand,
      vehicleCount,
      vehicleCapacity,
    };

    setHistory((prev) => {
      // Remove any states after current index (when undoing then making new changes)
      const newHistory = prev.slice(0, historyIndex + 1);
      // Add new state
      const updatedHistory = [...newHistory, currentState];
      // Limit history to 50 states to prevent memory issues
      return updatedHistory.slice(-50);
    });
    setHistoryIndex((prev) => Math.min(prev + 1, 49));
  }, [routeName, startTime, endTime, stopConfigs, segments, trafficMultiplier, isCirculator, reverseReturnJourney, isBusRoute, freehand, vehicleCount, vehicleCapacity, isEditing, isCreating, historyIndex]);

  // Undo function
  const handleUndo = useCallback(() => {
    if (historyIndex <= 0 || !isEditing || isCreating) return;

    isUndoRedoRef.current = true;
    const previousState = history[historyIndex - 1];
    
    setRouteName(previousState.routeName);
    setStartTime(previousState.startTime);
    setEndTime(previousState.endTime);
    setStopConfigs(previousState.stopConfigs);
    setSegments(previousState.segments);
    setTrafficMultiplier(previousState.trafficMultiplier);
    setIsCirculator(previousState.isCirculator);
    setReverseReturnJourney(previousState.reverseReturnJourney);
    setIsBusRoute(previousState.isBusRoute);
    setFreehand(previousState.freehand ?? false);
    setVehicleCount(previousState.vehicleCount);
    setVehicleCapacity(previousState.vehicleCapacity);
    
    setHistoryIndex((prev) => prev - 1);
    
    // Reset flag after a short delay to allow state updates
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);
  }, [history, historyIndex, isEditing, isCreating]);

  // Redo function
  const handleRedo = useCallback(() => {
    if (historyIndex >= history.length - 1 || !isEditing || isCreating) return;

    isUndoRedoRef.current = true;
    const nextState = history[historyIndex + 1];
    
    setRouteName(nextState.routeName);
    setStartTime(nextState.startTime);
    setEndTime(nextState.endTime);
    setStopConfigs(nextState.stopConfigs);
    setSegments(nextState.segments);
    setTrafficMultiplier(nextState.trafficMultiplier);
    setIsCirculator(nextState.isCirculator);
    setReverseReturnJourney(nextState.reverseReturnJourney);
    setIsBusRoute(nextState.isBusRoute);
    setFreehand(nextState.freehand ?? false);
    setVehicleCount(nextState.vehicleCount);
    setVehicleCapacity(nextState.vehicleCapacity);
    
    setHistoryIndex((prev) => prev + 1);
    
    // Reset flag after a short delay to allow state updates
    setTimeout(() => {
      isUndoRedoRef.current = false;
    }, 0);
  }, [history, historyIndex, isEditing, isCreating]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere with input fields
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Allow undo/redo in text inputs (browser default)
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
          return; // Let browser handle it
        }
        return;
      }

      // Ctrl+Z or Cmd+Z for undo
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }

      // Ctrl+Y or Ctrl+Shift+Z or Cmd+Shift+Z for redo
      if (
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') ||
        ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && e.shiftKey)
      ) {
        e.preventDefault();
        handleRedo();
        return;
      }

      // 'E' key to toggle edit mode when a route is selected (only when not typing in an input)
      if (
        e.key.toLowerCase() === 'e' &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.shiftKey &&
        selectedRoute &&
        !isCreating &&
        (e.target as HTMLElement).tagName !== 'INPUT' &&
        (e.target as HTMLElement).tagName !== 'TEXTAREA'
      ) {
        e.preventDefault();
        if (editingRouteId === selectedRoute.id) {
          exitEditMode();
        } else {
          if (selectedRoute.stops.length < 2) {
            alert(t('routeBuilder.alerts.minStopsForEdit'));
            return;
          }
          enterEditMode(selectedRoute.id);
        }
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo, selectedRoute, editingRouteId, enterEditMode, exitEditMode, isCreating]);

  const handleCreateNew = useCallback(() => {
    setRouteName('');
    setStartTime(DEFAULT_ROUTE_START_TIME);
    setEndTime('');
    setSelectedStopId('');
    setStopConfigs([]);
    setTrafficMultiplier(DEFAULT_TRAFFIC_MULTIPLIER);
    setSegments([]);
    setIsCirculator(false);
    setReverseReturnJourney(true);
    setIsBusRoute(false);
    setFreehand(false);
    setVehicleCount(1); // Default to 1 vehicle
    setVehicleCapacity(50); // Reset to default capacity
    setServiceDays(undefined);
    setTripHeadsign('');
    setRouteDesc('');
    setSelectedRoute(null);
    setSelectedRouteId(null);
    setIsEditing(false);
    setIsCreating(true);
    setEditorTab(0);
    routeNameManuallySet.current = false;
    lastAutoGeneratedName.current = '';
    setManuallyEditedSegments(new Set()); // Reset manually edited segments
    
    // Reset history for new route
    setHistory([]);
    setHistoryIndex(-1);
    // New route creation should immediately allow map clicks to add stops.
    setIsRouteCaptureMode(true);
    setCapturePoints([]);
    setCaptureWaypoints([]);
    setCaptureWaypointStopIndices([]);
    setIsCaptureBusy(false);
    clearDraftRouteTracking();
    circulatorReturnLegCacheRef.current = null;
    captureSessionIdRef.current += 1;
    capturePendingQueueRef.current = [];
    capturePreparedMapRef.current.clear();
    captureSequenceRef.current = 0;
    captureNextCommitSeqRef.current = 1;
    captureActivePreprocessRef.current = 0;
    if (captureCommitTimeoutRef.current) {
      clearTimeout(captureCommitTimeoutRef.current);
      captureCommitTimeoutRef.current = null;
    }
    captureCommitScheduledRef.current = false;
    window.dispatchEvent(
      new CustomEvent('startFixedRouteCapture', {
        detail: { sessionId: captureSessionIdRef.current },
      })
    );
  }, [setSelectedRouteId, clearDraftRouteTracking]);

  const isRouteCaptureModeRef = useRef(isRouteCaptureMode);
  const stopConfigsRef = useRef(stopConfigs);
  const segmentsRef = useRef(segments);
  const stopsRef = useRef(stops);
  const capturePointsRef = useRef(capturePoints);
  const captureWaypointsRef = useRef(captureWaypoints);
  const captureWaypointStopIndicesRef = useRef(captureWaypointStopIndices);
  const captureSessionIdRef = useRef(0);
  const captureSequenceRef = useRef(0);
  const captureNextCommitSeqRef = useRef(1);
  const capturePendingQueueRef = useRef<QueuedCaptureEvent[]>([]);
  const capturePreparedMapRef = useRef<Map<number, PreparedCaptureEvent>>(new Map());
  const captureActivePreprocessRef = useRef(0);
  const captureCommitScheduledRef = useRef(false);
  const captureCommitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushScheduledDraftUpsertRef = useRef<() => void>(() => {});

  useEffect(() => {
    isRouteCaptureModeRef.current = isRouteCaptureMode;
  }, [isRouteCaptureMode]);
  useEffect(() => {
    stopConfigsRef.current = stopConfigs;
  }, [stopConfigs]);
  useEffect(() => {
    const cache = circulatorReturnLegCacheRef.current;
    if (cache && cache.stopSequenceKey !== getStopSequenceKey(stopConfigs)) {
      circulatorReturnLegCacheRef.current = null;
    }
  }, [stopConfigs]);
  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);
  useEffect(() => {
    stopsRef.current = stops;
  }, [stops]);
  useEffect(() => {
    capturePointsRef.current = capturePoints;
  }, [capturePoints]);
  useEffect(() => {
    captureWaypointsRef.current = captureWaypoints;
  }, [captureWaypoints]);
  useEffect(() => {
    captureWaypointStopIndicesRef.current = captureWaypointStopIndices;
  }, [captureWaypointStopIndices]);

  const finishRouteCapture = useCallback(() => {
    if (!isRouteCaptureMode) return;
    if (draftUpsertTimeoutRef.current) {
      clearTimeout(draftUpsertTimeoutRef.current);
      draftUpsertTimeoutRef.current = null;
    }
    flushScheduledDraftUpsertRef.current();
    captureSessionIdRef.current += 1;
    capturePendingQueueRef.current = [];
    capturePreparedMapRef.current.clear();
    captureSequenceRef.current = 0;
    captureNextCommitSeqRef.current = 1;
    captureActivePreprocessRef.current = 0;
    if (captureCommitTimeoutRef.current) {
      clearTimeout(captureCommitTimeoutRef.current);
      captureCommitTimeoutRef.current = null;
    }
    captureCommitScheduledRef.current = false;
    setIsRouteCaptureMode(false);
    setIsCaptureBusy(false);
  }, [isRouteCaptureMode]);

  const startRouteCapture = useCallback(() => {
    captureSessionIdRef.current += 1;
    capturePendingQueueRef.current = [];
    capturePreparedMapRef.current.clear();
    captureSequenceRef.current = 0;
    captureNextCommitSeqRef.current = 1;
    captureActivePreprocessRef.current = 0;
    if (captureCommitTimeoutRef.current) {
      clearTimeout(captureCommitTimeoutRef.current);
      captureCommitTimeoutRef.current = null;
    }
    captureCommitScheduledRef.current = false;
    setIsRouteCaptureMode(true);
  }, []);

  const cancelRouteCapture = useCallback(() => {
    if (draftUpsertTimeoutRef.current) {
      clearTimeout(draftUpsertTimeoutRef.current);
      draftUpsertTimeoutRef.current = null;
    }
    pendingDraftUpsertRef.current = null;
    captureSessionIdRef.current += 1;
    capturePendingQueueRef.current = [];
    capturePreparedMapRef.current.clear();
    captureSequenceRef.current = 0;
    captureNextCommitSeqRef.current = 1;
    captureActivePreprocessRef.current = 0;
    if (captureCommitTimeoutRef.current) {
      clearTimeout(captureCommitTimeoutRef.current);
      captureCommitTimeoutRef.current = null;
    }
    captureCommitScheduledRef.current = false;
    setIsRouteCaptureMode(false);
    setIsCaptureBusy(false);
    window.dispatchEvent(
      new CustomEvent('cancelFixedRouteCapture', {
        detail: { sessionId: captureSessionIdRef.current },
      })
    );
  }, []);

  const didInitializeViewRef = useRef(false);
  useEffect(() => {
    // Only initialize/create capture state when the fixed-routes drawer is actually open.
    if (!isDrawerOpen) {
      return;
    }

    if (didInitializeViewRef.current) {
      return;
    }
    didInitializeViewRef.current = true;

    if (routes.length === 0) {
      handleCreateNew();
      return;
    }

    // When returning to Routes tab with existing routes, default to the overview list.
    cancelRouteCapture();
    circulatorReturnLegCacheRef.current = null;
    setIsEditing(false);
    setIsCreating(false);
    setSelectedRoute(null);
    setSelectedRouteId(null);
    setEditorTab(0);
    setCapturePoints([]);
    setCaptureWaypoints([]);
    setCaptureWaypointStopIndices([]);
    clearDraftRouteTracking();
  }, [isDrawerOpen, routes.length, handleCreateNew, cancelRouteCapture, setSelectedRouteId, clearDraftRouteTracking]);

  // Keep map capture mode in sync with local capture state.
  // This avoids the "Capture on" UI getting out of sync with the map's click interception
  // (e.g. if the initial start event fires before listeners are attached).
  useEffect(() => {
    if (isRouteCaptureMode) {
      window.dispatchEvent(
        new CustomEvent('startFixedRouteCapture', {
          detail: { sessionId: captureSessionIdRef.current },
        })
      );
    } else {
      window.dispatchEvent(
        new CustomEvent('finishFixedRouteCapture', {
          detail: { sessionId: captureSessionIdRef.current },
        })
      );
    }
  }, [isRouteCaptureMode]);

  const toggleRouteCapture = useCallback(() => {
    if (isRouteCaptureMode) {
      finishRouteCapture();
      return;
    }
    startRouteCapture();
  }, [isRouteCaptureMode, finishRouteCapture, startRouteCapture]);

  const wasDrawerOpenRef = useRef<boolean>(isDrawerOpen);
  useEffect(() => {
    // Auto-capture should only happen when the drawer opens (false -> true).
    if (!wasDrawerOpenRef.current && isDrawerOpen) {
      captureSessionIdRef.current += 1;
      capturePendingQueueRef.current = [];
      capturePreparedMapRef.current.clear();
      captureSequenceRef.current = 0;
      captureNextCommitSeqRef.current = 1;
      captureActivePreprocessRef.current = 0;
      if (captureCommitTimeoutRef.current) {
        clearTimeout(captureCommitTimeoutRef.current);
        captureCommitTimeoutRef.current = null;
      }
      captureCommitScheduledRef.current = false;
      setIsRouteCaptureMode(true);
      setIsCaptureBusy(false);
    }
    // Ensure capture is off when drawer closes.
    if (wasDrawerOpenRef.current && !isDrawerOpen) {
      captureSessionIdRef.current += 1;
      capturePendingQueueRef.current = [];
      capturePreparedMapRef.current.clear();
      captureSequenceRef.current = 0;
      captureNextCommitSeqRef.current = 1;
      captureActivePreprocessRef.current = 0;
      if (captureCommitTimeoutRef.current) {
        clearTimeout(captureCommitTimeoutRef.current);
        captureCommitTimeoutRef.current = null;
      }
      captureCommitScheduledRef.current = false;
      setIsRouteCaptureMode(false);
      setIsCaptureBusy(false);
    }
    wasDrawerOpenRef.current = isDrawerOpen;
  }, [isDrawerOpen]);

  const upsertDraftRoute = useCallback((
    nextStops: FixedRouteStopConfig[],
    nextWaypoints: [number, number][],
    nextWaypointStopIndices: number[],
    nextSegments?: FixedRouteSegment[],
    options?: {
      isCirculatorOverride?: boolean;
      endTimeOverride?: string;
      reverseReturnJourneyOverride?: boolean;
    }
  ) => {
    // Prefer the canonical route in context state over local `selectedRoute` to avoid
    // accidentally stamping over fields that are assigned by the provider (e.g. color).
    // Refs mirror the draft synchronously: multiple capture commits in one tick run before
    // React re-renders, so state-only draftRouteId/routes would incorrectly call addRoute again.
    const candidateRouteId =
      draftRouteIdRef.current ?? draftRouteId ?? selectedRoute?.id ?? null;
    const routeFromStore = candidateRouteId ? (routes.find((r) => r.id === candidateRouteId) ?? null) : null;
    const selectedMatches =
      selectedRoute && candidateRouteId === selectedRoute.id ? selectedRoute : null;
    const snapshotMatches =
      draftRouteSnapshotRef.current && candidateRouteId === draftRouteSnapshotRef.current.id
        ? draftRouteSnapshotRef.current
        : null;
    const existingRoute = routeFromStore ?? selectedMatches ?? snapshotMatches ?? null;
    const routeId =
      existingRoute?.id || draftRouteIdRef.current || draftRouteId || `route-${Date.now()}`;
    const routeSegments = (nextSegments && nextSegments.length > 0 ? nextSegments : segments).length > 0
      ? (nextSegments && nextSegments.length > 0 ? nextSegments : segments)
      : nextStops.slice(0, -1).map((stop, index) => ({
      fromStopId: stop.stopId,
      toStopId: nextStops[index + 1].stopId,
      travelTimeMinutes: 5,
    }));

    const effectiveCirculator = options?.isCirculatorOverride ?? isCirculator;
    const effectiveEndTime = options?.endTimeOverride ?? endTime;
    const effectiveReverseReturnJourney = options?.reverseReturnJourneyOverride ?? reverseReturnJourney;

    const routeDraft: FixedRoute = {
      id: routeId,
      name: routeName.trim() || existingRoute?.name || t('routeBuilder.defaults.newRouteName'),
      shortName: existingRoute?.shortName,
      startTime,
      endTime: effectiveEndTime || undefined,
      stops: nextStops,
      segments: routeSegments,
      trafficMultiplier: trafficMultiplier !== DEFAULT_TRAFFIC_MULTIPLIER ? trafficMultiplier : undefined,
      isCirculator: effectiveCirculator,
      reverseReturnJourney: effectiveReverseReturnJourney,
      isBusRoute,
      vehicleCount: vehicleCount && vehicleCount >= 1 ? vehicleCount : (effectiveCirculator ? undefined : 1),
      vehicleCapacity: vehicleCapacity && vehicleCapacity > 0 ? vehicleCapacity : 50,
      waypoints: undefined,
      waypointStopIndices: undefined,
      // Keep previous geometry metadata so map sync can do incremental recalculation
      // (e.g. only the newly-added leg during capture) instead of full-route rerouting.
      geometry: existingRoute?.geometry,
      // Preserve provider-assigned fields where possible (notably `color`).
      // For brand-new drafts, provide a stable fallback so subsequent updates
      // don't keep writing `undefined` and collapsing to the same default color.
      color: existingRoute?.color || generateRouteColor(routeId),
      width: existingRoute?.width,
      hidden: existingRoute?.hidden,
      lineId: existingRoute?.lineId,
      layerId: existingRoute?.layerId,
      isManuallyEdited: existingRoute?.isManuallyEdited,
      // GTFS metadata: editable fields from Route Details state; the rest is
      // preserved from the existing route so editing an imported route never
      // drops its captured GTFS data (route_type, agency, schedules, calendar).
      serviceDays: serviceDays ?? existingRoute?.serviceDays,
      serviceDateRange: existingRoute?.serviceDateRange,
      serviceExceptions: existingRoute?.serviceExceptions,
      tripHeadsign: tripHeadsign.trim() || existingRoute?.tripHeadsign,
      routeDesc: routeDesc.trim() || existingRoute?.routeDesc,
      gtfsRouteId: existingRoute?.gtfsRouteId,
      gtfsRouteType: existingRoute?.gtfsRouteType,
      routeTextColor: existingRoute?.routeTextColor,
      gtfsAgency: existingRoute?.gtfsAgency,
      tripSchedules: existingRoute?.tripSchedules,
      // Preserve the live-network provenance so editing a forked copy keeps its
      // "edited from network" identity (PRD-04 FR15/FR16).
      networkOrigin: existingRoute?.networkOrigin,
    };

    if (existingRoute) {
      updateRoute(routeDraft);
    } else {
      addRoute(routeDraft);
    }

    draftRouteIdRef.current = routeId;
    draftRouteSnapshotRef.current = routeDraft;
    setDraftRouteId(routeId);
    setSelectedRoute(routeDraft);
    setSelectedRouteId(routeId);
  }, [
    selectedRoute,
    draftRouteId,
    routes,
    segments,
    routeName,
    startTime,
    endTime,
    trafficMultiplier,
    isCirculator,
    reverseReturnJourney,
    isBusRoute,
    vehicleCount,
    vehicleCapacity,
    updateRoute,
    addRoute,
    setSelectedRouteId,
  ]);

  const pendingDraftUpsertRef = useRef<DraftUpsertArgs | null>(null);
  const draftUpsertTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushScheduledDraftUpsert = useCallback(() => {
    const pending = pendingDraftUpsertRef.current;
    pendingDraftUpsertRef.current = null;
    if (!pending) return;
    upsertDraftRoute(
      pending.nextStops,
      pending.nextWaypoints,
      pending.nextWaypointStopIndices,
      pending.nextSegments,
      pending.options
    );
  }, [upsertDraftRoute]);

  useEffect(() => {
    flushScheduledDraftUpsertRef.current = flushScheduledDraftUpsert;
  }, [flushScheduledDraftUpsert]);

  const scheduleDraftUpsert = useCallback((
    nextStops: FixedRouteStopConfig[],
    nextWaypoints: [number, number][],
    nextWaypointStopIndices: number[],
    nextSegments?: FixedRouteSegment[],
    options?: {
      isCirculatorOverride?: boolean;
      endTimeOverride?: string;
      reverseReturnJourneyOverride?: boolean;
    }
  ) => {
    pendingDraftUpsertRef.current = {
      nextStops,
      nextWaypoints,
      nextWaypointStopIndices,
      nextSegments,
      options,
    };

    if (!isRouteCaptureModeRef.current) {
      if (draftUpsertTimeoutRef.current) {
        clearTimeout(draftUpsertTimeoutRef.current);
        draftUpsertTimeoutRef.current = null;
      }
      flushScheduledDraftUpsert();
      return;
    }

    if (draftUpsertTimeoutRef.current) return;
    draftUpsertTimeoutRef.current = setTimeout(() => {
      draftUpsertTimeoutRef.current = null;
      flushScheduledDraftUpsert();
    }, CAPTURE_DRAFT_PERSIST_DEBOUNCE_MS);
  }, [flushScheduledDraftUpsert]);

  useEffect(() => {
    return () => {
      if (draftUpsertTimeoutRef.current) {
        clearTimeout(draftUpsertTimeoutRef.current);
        draftUpsertTimeoutRef.current = null;
      }
      if (captureCommitTimeoutRef.current) {
        clearTimeout(captureCommitTimeoutRef.current);
        captureCommitTimeoutRef.current = null;
      }
      captureCommitScheduledRef.current = false;
      pendingDraftUpsertRef.current = null;
    };
  }, []);

  const refreshCaptureBusyState = useCallback(() => {
    const busy =
      captureActivePreprocessRef.current > 0 ||
      capturePendingQueueRef.current.length > 0 ||
      capturePreparedMapRef.current.size > 0;
    setIsCaptureBusy(busy);
  }, []);

  const updateCapturedSegmentTiming = useCallback(async (
    sessionId: number,
    fromStop: FixedRouteStop,
    toStop: FixedRouteStop,
    fromStopId: string,
    toStopId: string
  ) => {
    try {
      const timingResult = await Promise.race([
        isBusRoute
          ? hereCalculateTravelTimeAndDistance(fromStop, toStop, true)
          : osrmCalculateTravelTimeAndDistance(fromStop, toStop, 'driving'),
        new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), CAPTURE_SEGMENT_RENDER_TIMEOUT_MS);
        }),
      ]);
      if (captureSessionIdRef.current !== sessionId) return;
      if (!timingResult) return;
      const result = timingResult;
      const cacheKey = `${fromStopId}-${toStopId}`;
      distanceTimeCacheRef.current.set(cacheKey, result);
      setSegments((prevSegments) => {
        const nextSegments = [...prevSegments];
        const existingSegmentIndex = nextSegments.findIndex(
          (seg) => seg.fromStopId === fromStopId && seg.toStopId === toStopId
        );
        const timedSegment: FixedRouteSegment = {
          fromStopId,
          toStopId,
          travelTimeMinutes: result.travelTimeMinutes,
          distanceMeters: result.distanceMeters,
        };
        if (existingSegmentIndex >= 0) {
          nextSegments[existingSegmentIndex] = timedSegment;
        } else {
          nextSegments.push(timedSegment);
        }
        segmentsRef.current = nextSegments;
        scheduleDraftUpsert(
          stopConfigsRef.current,
          captureWaypointsRef.current,
          captureWaypointStopIndicesRef.current,
          nextSegments
        );
        return nextSegments;
      });
    } catch {
      // Keep fallback segment if timing call fails.
    } finally {
      if (captureSessionIdRef.current !== sessionId) return;
      window.dispatchEvent(
        new CustomEvent('fixedRouteCaptureSegmentRendered', {
          detail: {
            sessionId,
            fromStopId,
            toStopId,
          },
        })
      );
    }
  }, [isBusRoute, scheduleDraftUpsert]);

  const commitPreparedCaptureEvent = useCallback((prepared: PreparedCaptureEvent) => {
    if (prepared.sessionId !== captureSessionIdRef.current) return;
    if (!isRouteCaptureModeRef.current) return;

    if (prepared.type === 'waypoint') {
      const nextCapturePoints = [
        ...capturePointsRef.current,
        { type: 'waypoint' as const, coordinates: prepared.snappedPoint },
      ];
      capturePointsRef.current = nextCapturePoints;
      setCapturePoints(nextCapturePoints);

      const nextWaypoints = [...captureWaypointsRef.current, prepared.snappedPoint];
      captureWaypointsRef.current = nextWaypoints;
      setCaptureWaypoints(nextWaypoints);
      const anchorSegmentIndex = Math.max(0, stopConfigsRef.current.length - 1);
      const nextWaypointStopIndices = [...captureWaypointStopIndicesRef.current, anchorSegmentIndex];
      captureWaypointStopIndicesRef.current = nextWaypointStopIndices;
      setCaptureWaypointStopIndices(nextWaypointStopIndices);
      scheduleDraftUpsert(stopConfigsRef.current, nextWaypoints, nextWaypointStopIndices, segmentsRef.current);
      return;
    }

    const initialPoint = prepared.rawPoint ?? prepared.snappedPoint;
    const currentStops = stopsRef.current.filter(hasValidStopCoordinates);
    const currentStopConfigs = stopConfigsRef.current;
    const matchedExistingRouteStop = findNearestExistingRouteStop(
      initialPoint,
      currentStopConfigs,
      currentStops,
      CAPTURE_EXISTING_STOP_SNAP_DISTANCE_METERS
    );
    const matchedExistingStop = matchedExistingRouteStop ?? findNearestExistingStop(
      initialPoint,
      currentStops,
      CAPTURE_EXISTING_STOP_SNAP_DISTANCE_METERS
    );

    const targetStop: FixedRouteStop = matchedExistingStop ?? {
      id: `stop-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: prepared.stopName || t('routeBuilder.defaults.meetingPoint'),
      coordinates: {
        lat: initialPoint[0],
        lng: initialPoint[1],
      },
    };

    if (!matchedExistingStop) {
      addStop(targetStop);

      // Resolve the stop NAME and the road-SNAPPED position INDEPENDENTLY. Previously naming
      // awaited the snap call first, so a slow/unavailable ORS snap blocked auto-naming and stops
      // stayed "Meeting Point". Both updates apply by stop id off the latest stops (so they survive
      // finishing the route, are a no-op if the stop was cancelled, and won't clobber a value the
      // user has since edited).
      void (async () => {
        try {
          const resolvedName = await getOsmStopName(initialPoint);
          if (!resolvedName || !resolvedName.trim()) return;
          const latest = stopsRef.current.find((s) => s.id === targetStop.id);
          if (!latest) return;
          if (latest.name && latest.name !== targetStop.name) return; // user renamed it
          updateStop({ ...latest, name: resolvedName });
        } catch {
          // Keep fallback name if reverse geocoding fails.
        }
      })();

      if (!isFreehandRef.current) {
        void (async () => {
          try {
            const snappedPoint = await snapPointToRoad(initialPoint);
            const latest = stopsRef.current.find((s) => s.id === targetStop.id);
            if (!latest) return;
            updateStop({ ...latest, coordinates: { lat: snappedPoint[0], lng: snappedPoint[1] } });
          } catch {
            // Snap unavailable — keep the clicked location.
          }
        })();
      }
    }

    window.dispatchEvent(
      new CustomEvent('fixedRouteCaptureStopCommitted', {
        detail: {
          sessionId: prepared.sessionId,
          stopId: targetStop.id,
        },
      })
    );

    const requestedInsertIndex = findInsertionIndexForCapturedStop(
      initialPoint,
      currentStopConfigs,
      currentStops,
      targetStop
    );
    const existingTargetIndex = currentStopConfigs.findIndex((stopConfig) => stopConfig.stopId === targetStop.id);
    const reorderedStopConfigs = [...currentStopConfigs];
    let reusedStopConfig: FixedRouteStopConfig | null = null;
    if (existingTargetIndex >= 0) {
      const [removed] = reorderedStopConfigs.splice(existingTargetIndex, 1);
      if (removed) reusedStopConfig = removed;
    }
    let normalizedInsertIndex = requestedInsertIndex;
    if (existingTargetIndex >= 0 && existingTargetIndex < requestedInsertIndex) {
      normalizedInsertIndex -= 1;
    }
    normalizedInsertIndex = Math.max(0, Math.min(normalizedInsertIndex, reorderedStopConfigs.length));

    reorderedStopConfigs.splice(normalizedInsertIndex, 0, {
      stopId: targetStop.id,
      waitTimeMinutes: reusedStopConfig?.waitTimeMinutes ?? DEFAULT_WAIT_TIME_MINUTES,
    });

    const nextStopConfigs = dedupeStopConfigsByStopId(reorderedStopConfigs);
    stopConfigsRef.current = nextStopConfigs;
    setStopConfigs(nextStopConfigs);

    const nextCapturePoints = [
      ...capturePointsRef.current,
      {
        type: 'stop' as const,
        coordinates: [targetStop.coordinates.lat, targetStop.coordinates.lng] as [number, number],
      },
    ];
    capturePointsRef.current = nextCapturePoints;
    setCapturePoints(nextCapturePoints);

    const previousSegmentsByKey = new Map(
      segmentsRef.current.map((segment) => [`${segment.fromStopId}->${segment.toStopId}`, segment] as const)
    );
    const nextSegments: FixedRouteSegment[] = [];
    for (let segmentIndex = 0; segmentIndex < nextStopConfigs.length - 1; segmentIndex += 1) {
      const fromStopId = nextStopConfigs[segmentIndex].stopId;
      const toStopId = nextStopConfigs[segmentIndex + 1].stopId;
      const existingSegment = previousSegmentsByKey.get(`${fromStopId}->${toStopId}`);
      nextSegments.push(
        existingSegment ?? {
          fromStopId,
          toStopId,
          travelTimeMinutes: 5,
        }
      );
    }
    segmentsRef.current = nextSegments;
    setSegments(nextSegments);

    for (const segment of nextSegments) {
      // Only (re)compute travel time for NEW / not-yet-timed links. Adding a stop must not
      // re-time every existing leg — appending re-times just the new last leg, and inserting
      // mid-route re-times just the two split legs. A leg is already timed if it carries real
      // distance data (not the placeholder default) or is in the distance/time cache.
      const reusedSegment = previousSegmentsByKey.get(`${segment.fromStopId}->${segment.toStopId}`);
      const alreadyTimed =
        (reusedSegment != null && reusedSegment.distanceMeters != null) ||
        distanceTimeCacheRef.current.has(`${segment.fromStopId}-${segment.toStopId}`);
      if (alreadyTimed) {
        continue;
      }
      const fromStop = resolveStopForRouteOrdering(segment.fromStopId, currentStops, targetStop);
      const toStop = resolveStopForRouteOrdering(segment.toStopId, currentStops, targetStop);
      if (fromStop?.coordinates && toStop?.coordinates) {
        void updateCapturedSegmentTiming(prepared.sessionId, fromStop, toStop, segment.fromStopId, segment.toStopId);
      } else {
        window.dispatchEvent(
          new CustomEvent('fixedRouteCaptureSegmentRendered', {
            detail: {
              sessionId: prepared.sessionId,
              fromStopId: segment.fromStopId,
              toStopId: segment.toStopId,
            },
          })
        );
      }
    }

    scheduleDraftUpsert(
      nextStopConfigs,
      captureWaypointsRef.current,
      captureWaypointStopIndicesRef.current,
      nextSegments
    );
  }, [addStop, updateStop, scheduleDraftUpsert, updateCapturedSegmentTiming]);

  const commitNextPreparedCaptureEvent = useCallback((): boolean => {
    const nextSeq = captureNextCommitSeqRef.current;
    const prepared = capturePreparedMapRef.current.get(nextSeq);
    if (!prepared) return false;
    capturePreparedMapRef.current.delete(nextSeq);
    commitPreparedCaptureEvent(prepared);
    captureNextCommitSeqRef.current += 1;
    return true;
  }, [commitPreparedCaptureEvent]);

  const schedulePreparedCaptureCommit = useCallback(() => {
    if (captureCommitScheduledRef.current) return;
    captureCommitScheduledRef.current = true;
    captureCommitTimeoutRef.current = setTimeout(() => {
      captureCommitTimeoutRef.current = null;
      captureCommitScheduledRef.current = false;
      const committed = commitNextPreparedCaptureEvent();
      refreshCaptureBusyState();
      if (!committed) return;
      const nextSeq = captureNextCommitSeqRef.current;
      if (capturePreparedMapRef.current.has(nextSeq)) {
        schedulePreparedCaptureCommit();
      }
    }, 0);
  }, [commitNextPreparedCaptureEvent, refreshCaptureBusyState]);

  const preprocessCaptureEvent = useCallback(async (event: QueuedCaptureEvent): Promise<PreparedCaptureEvent | null> => {
    const rawPoint: [number, number] = [event.lat, event.lng];

    if (event.sessionId !== captureSessionIdRef.current) {
      return null;
    }

    if (event.type === 'stop') {
      return {
        seq: event.seq,
        sessionId: event.sessionId,
        type: 'stop',
        rawPoint,
        // Stop creation is optimistic; snap happens after commit.
        snappedPoint: rawPoint,
      };
    }

    let snappedPoint: [number, number] = rawPoint;
    try {
      snappedPoint = await snapPointToRoad(rawPoint);
    } catch {
      snappedPoint = rawPoint;
    }

    if (event.type === 'waypoint') {
      return {
        seq: event.seq,
        sessionId: event.sessionId,
        type: 'waypoint',
        snappedPoint,
      };
    }

    if (event.sessionId !== captureSessionIdRef.current) {
      return null;
    }

    return null;
  }, []);

  const processCaptureQueue = useCallback(() => {
    while (
      captureActivePreprocessRef.current < CAPTURE_PREPROCESS_CONCURRENCY &&
      capturePendingQueueRef.current.length > 0
    ) {
      const event = capturePendingQueueRef.current.shift();
      if (!event) break;
      captureActivePreprocessRef.current += 1;
      void preprocessCaptureEvent(event)
        .then((prepared) => {
          if (!prepared) return;
          if (prepared.sessionId !== captureSessionIdRef.current) return;
          capturePreparedMapRef.current.set(prepared.seq, prepared);
          schedulePreparedCaptureCommit();
        })
        .finally(() => {
          captureActivePreprocessRef.current = Math.max(0, captureActivePreprocessRef.current - 1);
          refreshCaptureBusyState();
          processCaptureQueue();
        });
    }
    refreshCaptureBusyState();
  }, [preprocessCaptureEvent, schedulePreparedCaptureCommit, refreshCaptureBusyState]);

  const enqueueCaptureEvent = useCallback((type: CaptureEventType, lat: number, lng: number) => {
    if (!isRouteCaptureModeRef.current) return;

    const sessionId = captureSessionIdRef.current;
    const seq = ++captureSequenceRef.current;

    // Stop capture no longer needs async preprocess (snap/geocode are async post-commit),
    // so enqueue directly into the ordered commit stream to avoid burst-input drops.
    if (type === 'stop') {
      capturePreparedMapRef.current.set(seq, {
        seq,
        sessionId,
        type: 'stop',
        rawPoint: [lat, lng],
        snappedPoint: [lat, lng],
      });
      refreshCaptureBusyState();
      schedulePreparedCaptureCommit();
      return;
    }

    capturePendingQueueRef.current.push({ seq, sessionId, type, lat, lng });
    refreshCaptureBusyState();
    processCaptureQueue();
  }, [processCaptureQueue, refreshCaptureBusyState, schedulePreparedCaptureCommit]);

  const handleCaptureLeftClick = useCallback((lat: number, lng: number) => {
    if (!isRouteCaptureModeRef.current) return;

    const disableNearStopFinish = Boolean(
      (window as Window & { [CAPTURE_DISABLE_NEAR_STOP_FINISH_FLAG]?: boolean })[CAPTURE_DISABLE_NEAR_STOP_FINISH_FLAG]
    );
    const lastStopConfig = stopConfigsRef.current[stopConfigsRef.current.length - 1];
    if (!disableNearStopFinish && lastStopConfig) {
      const lastStop = stopsRef.current.find((s) => s.id === lastStopConfig.stopId);
      if (lastStop?.coordinates) {
        const clickPoint: [number, number] = [lat, lng];
        const lastStopPoint: [number, number] = [lastStop.coordinates.lat, lastStop.coordinates.lng];
        if (getDistanceMeters(clickPoint, lastStopPoint) <= CAPTURE_FINISH_DISTANCE_METERS) {
          finishRouteCapture();
          return;
        }
      }
    }

    window.dispatchEvent(
      new CustomEvent('fixedRouteCaptureStopQueued', {
        detail: {
          sessionId: captureSessionIdRef.current,
          lat,
          lng,
        },
      })
    );
    enqueueCaptureEvent('stop', lat, lng);
  }, [finishRouteCapture, enqueueCaptureEvent]);

  const handleCaptureRightClick = useCallback((lat: number, lng: number) => {
    // Waypoint capture is disabled.
  }, []);

  useEffect(() => {
    const onLeftClick = (event: Event) => {
      const customEvent = event as CustomEvent<{ lat: number; lng: number }>;
      if (!customEvent.detail) return;
      handleCaptureLeftClick(customEvent.detail.lat, customEvent.detail.lng);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        finishRouteCapture();
      }
    };

    window.addEventListener('fixedRouteCaptureLeftClick', onLeftClick as EventListener);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('fixedRouteCaptureLeftClick', onLeftClick as EventListener);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [handleCaptureLeftClick, finishRouteCapture]);

  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent('cancelFixedRouteCapture', {
          detail: { sessionId: captureSessionIdRef.current },
        })
      );
    };
  }, []);

  const handleContextMenu = (event: React.MouseEvent, route: FixedRoute) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            route,
          }
        : null
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleRouteSummary = () => {
    if (contextMenu) {
      setSelectedRouteId(contextMenu.route.id);
      handleCloseContextMenu();
    }
  };

  const handleEditRoute = () => {
    if (contextMenu) {
      handleSelectRoute(contextMenu.route);
      handleCloseContextMenu();
    }
  };

  const handleDuplicateRoute = () => {
    if (!contextMenu) return;
    
    const originalRoute = contextMenu.route;
    
    // Create a deep copy of the route with all properties
    const duplicatedRoute: FixedRoute = {
      ...originalRoute,
      id: `route-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, // Generate unique ID
      name: `${originalRoute.name}${t('routeBuilder.duplicateSuffix')}`,
      // Deep copy arrays to avoid reference sharing
      stops: originalRoute.stops.map(stop => ({ ...stop })),
      segments: originalRoute.segments.map(segment => ({ ...segment })),
      // Deep copy geometry if present
      geometry: originalRoute.geometry ? {
        ...originalRoute.geometry,
        coordinates: originalRoute.geometry.coordinates.map(coord => [...coord]),
      } : undefined,
      // Reset map-related properties for the duplicate
      lineId: undefined,
      layerId: undefined,
      // Clear UAT route ID so duplicate is treated as a new route, not a new version
      uatRouteId: undefined,
      // Clear UAT metadata since this is a new route
      uatMetadata: undefined,
    };
    
    // Insert the duplicated route right after the original route
    insertRoute(duplicatedRoute, originalRoute.id);
    
    handleCloseContextMenu();
    
    // Select the duplicated route so user can see/edit it immediately
    setSelectedRouteId(duplicatedRoute.id);
  };

  const handleExportRouteExcel = (route: FixedRoute) => {
    exportRouteToExcel(route, stops, globalTrafficMultiplier);
  };

  const handleExportRouteKML = (route: FixedRoute) => {
    exportRouteToKML(route, stops);
  };

  const handleSelectRoute = (route: FixedRoute) => {
    cancelRouteCapture();
    circulatorReturnLegCacheRef.current = null;
    setSelectedRoute(route);
    // Coerce to string: a route's name can arrive non-string from legacy/imported data,
    // and `routeName.trim()` runs during render — a number/undefined here would crash.
    const safeName = typeof route.name === 'string' ? route.name : String(route.name ?? '');
    setRouteName(safeName);
    setStartTime(route.startTime);
    const routeIsCirculator = route.isCirculator || false;
    const routeIsBusRoute = route.isBusRoute || false;
    // Set default end time to 22:00 for circulator routes if no end time is set
    setEndTime(route.endTime || (routeIsCirculator ? '22:00' : ''));
    setStopConfigs(route.stops);
    setTrafficMultiplier(route.trafficMultiplier ?? DEFAULT_TRAFFIC_MULTIPLIER);
    setSegments(route.segments || []);
    setIsCirculator(routeIsCirculator);
    setReverseReturnJourney(route.reverseReturnJourney ?? false);
    setIsBusRoute(routeIsBusRoute);
    setFreehand(route.freehand ?? false);
    setVehicleCount(route.vehicleCount ?? (routeIsCirculator ? 1 : 1)); // Default to 1 for both circulator and non-circulator
    setVehicleCapacity(route.vehicleCapacity ?? 50); // Default to 50 if not set
    setServiceDays(route.serviceDays);
    setTripHeadsign(route.tripHeadsign ?? '');
    setRouteDesc(route.routeDesc ?? '');
    setIsEditing(true);
    setIsCreating(false);
    setEditorTab(0);
    clearDraftRouteTracking();
    setCapturePoints([]);
    setCaptureWaypoints([]);
    setCaptureWaypointStopIndices([]);
    routeNameManuallySet.current = true; // Route already has a name, treat as manually set
    lastAutoGeneratedName.current = safeName;
    setManuallyEditedSegments(new Set()); // Reset manually edited segments when loading a route

    // Initialize history with current state
    const initialState: RouteState = {
      routeName: safeName,
      startTime: route.startTime,
      endTime: route.endTime || (routeIsCirculator ? '22:00' : ''),
      stopConfigs: [...route.stops],
      segments: [...(route.segments || [])],
      trafficMultiplier: route.trafficMultiplier ?? DEFAULT_TRAFFIC_MULTIPLIER,
      isCirculator: routeIsCirculator,
      reverseReturnJourney: route.reverseReturnJourney ?? false,
      isBusRoute: routeIsBusRoute,
      freehand: route.freehand ?? false,
      vehicleCount: route.vehicleCount,
      vehicleCapacity: route.vehicleCapacity,
    };
    setHistory([initialState]);
    setHistoryIndex(0);
    
    // If circulator route, ensure return segment is calculated (not using default)
    if (routeIsCirculator && route.stops.length >= 2) {
      const lastStopId = route.stops[route.stops.length - 1].stopId;
      const firstStopId = route.stops[0].stopId;
      const existingReturnSegment = route.segments?.find(
        (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
      );
      
      // If return segment doesn't exist or has default value (5 minutes), calculate it
      if (!existingReturnSegment || existingReturnSegment.travelTimeMinutes === 5) {
        setTimeout(() => {
          calculateReturnSegment();
        }, 300); // Delay to ensure state is fully loaded
      }
    }
  };

  const handleAddStop = (stopIdToAdd?: string) => {
    const stopId = stopIdToAdd ?? selectedStopId;

    if (!stopId || stopConfigs.find((s) => s.stopId === stopId)) {
      return;
    }

    const newStopConfig: FixedRouteStopConfig = {
      stopId,
      waitTimeMinutes: DEFAULT_WAIT_TIME_MINUTES,
    };

    const newStopConfigs = [...stopConfigs, newStopConfig];
    setStopConfigs(newStopConfigs);
    setSelectedStopId('');

    
    // If this is an existing route being edited, show rerendering notification
    if (selectedRoute) {
      setRouteGenerationStatus({ isGenerating: true, routeName: selectedRoute.name });
    }
    
    // Identify segments that need calculation after adding
    // The last segment (if there was one) and the new segment need calculation
    const changedSegmentKeys: string[] = [];
    
    if (newStopConfigs.length >= 2) {
      // The new last segment needs calculation
      const secondToLastStopId = newStopConfigs[newStopConfigs.length - 2].stopId;
      const lastStopId = newStopConfigs[newStopConfigs.length - 1].stopId;
      changedSegmentKeys.push(`${secondToLastStopId}-${lastStopId}`);
      
      // If there was a previous last segment, it needs recalculation too
      if (stopConfigs.length >= 2) {
        const oldSecondToLastStopId = stopConfigs[stopConfigs.length - 2].stopId;
        const oldLastStopId = stopConfigs[stopConfigs.length - 1].stopId;
        changedSegmentKeys.push(`${oldSecondToLastStopId}-${oldLastStopId}`);
      }
    }
    
    // Auto-calculate changed segments
    if (changedSegmentKeys.length > 0) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        handleCalculateChangedSegments(changedSegmentKeys);
      }, 100);
    }
    
    // If circulator route, calculate return segment after stops are added
    if (isCirculator && newStopConfigs.length >= 2) {
      setTimeout(() => {
        calculateReturnSegment();
      }, 200); // Delay slightly to ensure state is updated
    }
    
    // If editing an existing route, update it but keep existing geometry
    // The map sync will detect the stop change and use incremental update to recalculate only affected segments
    if (selectedRoute) {
      const updatedRoute: FixedRoute = {
        ...selectedRoute,
        stops: newStopConfigs,
        waypoints: undefined,
        waypointStopIndices: undefined,
        // Keep existing geometry - sync will detect change and do incremental update
        // This allows it to only recalculate the new segment instead of the entire route
      };
      updateRoute(updatedRoute);
    }
    
    // Save to history after a short delay (debounced)
    setTimeout(() => saveToHistory(), 100);
  };

  // F1: add a brand-new stop at a geocoded location, then reuse the existing add-stop path
  // (which appends the config and schedules segment timing for the new last segment).
  const handleAddStopFromSearch = (result: GeocodeResult) => {
    const newStop: FixedRouteStop = {
      id: uuidv4(),
      name: result.shortName,
      coordinates: { lat: result.lat, lng: result.lng },
    };
    addStop(newStop);
    logEvent({ category: 'fixed_routes', action: 'add_stop_by_search' });
    setAddressInput('');
    setAddressOptions([]);
    handleAddStop(newStop.id);
  };

  const addStopsToRoute = (stopsToAdd: FixedRouteStop[]) => {
    if (stopsToAdd.length === 0) {
      return;
    }

    const newStopConfigs: FixedRouteStopConfig[] = stopsToAdd.map((stop) => ({
      stopId: stop.id,
      waitTimeMinutes: DEFAULT_WAIT_TIME_MINUTES,
    }));

    const updatedStopConfigs = [...stopConfigs, ...newStopConfigs];
    setStopConfigs(updatedStopConfigs);

    if (selectedRoute) {
      setRouteGenerationStatus({ isGenerating: true, routeName: selectedRoute.name });
    }

    const changedSegmentKeys: string[] = [];

    for (let i = Math.max(0, stopConfigs.length - 1); i < updatedStopConfigs.length - 1; i++) {
      const fromStopId = updatedStopConfigs[i].stopId;
      const toStopId = updatedStopConfigs[i + 1].stopId;
      changedSegmentKeys.push(`${fromStopId}-${toStopId}`);
    }

    if (stopConfigs.length > 0 && stopsToAdd.length > 0) {
      const lastOriginalStopId = stopConfigs[stopConfigs.length - 1].stopId;
      const firstNewStopId = newStopConfigs[0].stopId;
      changedSegmentKeys.push(`${lastOriginalStopId}-${firstNewStopId}`);
    }

    if (changedSegmentKeys.length > 0) {
      setTimeout(() => {
        handleCalculateChangedSegments(changedSegmentKeys);
      }, 100);
    }

    if (isCirculator && updatedStopConfigs.length >= 2) {
      setTimeout(() => {
        calculateReturnSegment();
      }, 200);
    }

    if (selectedRoute) {
      const updatedRoute: FixedRoute = {
        ...selectedRoute,
        stops: updatedStopConfigs,
        waypoints: undefined,
        waypointStopIndices: undefined,
      };
      updateRoute(updatedRoute);
    }

    setTimeout(() => saveToHistory(), 100);
  };

  const handleAddAllStops = () => {
    const stopsToAdd = availableStops.filter(
      (stop) => !stopConfigs.find((s) => s.stopId === stop.id)
    );
    addStopsToRoute(stopsToAdd);
  };

  const handleAddStopsFromLayer = (layerId: number) => {
    const layer = appContext?.global.mapLayers.find((mapLayer) => mapLayer.layerId === layerId);
    if (!layer) {
      return;
    }

    const routeStopIds = new Set(stopConfigs.map((config) => config.stopId));
    const baseTimestamp = Date.now();
    const stopsToAdd: FixedRouteStop[] = [];

    layer.shapes
      .filter((shape): shape is Point => shape.type === 'point')
      .forEach((point, index) => {
        const existingStop = stops.find((stop) => stop.mapPointId === point.id);
        if (existingStop) {
          if (!routeStopIds.has(existingStop.id)) {
            stopsToAdd.push(existingStop);
          }
          return;
        }

        const newStop: FixedRouteStop = {
          id: `stop-${baseTimestamp}-${index}-${point.id}`,
          name: point.name || point.id,
          coordinates: {
            lat: point.coordinates[0],
            lng: point.coordinates[1],
          },
          mapPointId: point.id,
        };
        addStop(newStop);
        stopsToAdd.push(newStop);
      });

    addStopsToRoute(stopsToAdd);
  };

  const handleRemoveStop = (index: number) => {
    const removedStopId = stopConfigs[index]?.stopId;
    const newStopConfigs = stopConfigs.filter((_, i) => i !== index);
    setStopConfigs(newStopConfigs);
    
    // If this is an existing route being edited, show rerendering notification
    if (selectedRoute) {
      setRouteGenerationStatus({ isGenerating: true, routeName: selectedRoute.name });
    }
    
    // Remove segments that reference the removed stop
    const updatedSegments = segments.filter((seg) => {
      return seg.fromStopId !== removedStopId && seg.toStopId !== removedStopId;
    });
    
    // Identify segments that changed due to removal
    // The segments before and after the removed stop need recalculation
    const changedSegmentKeys: string[] = [];
    
    if (index > 0 && index < stopConfigs.length - 1) {
      // There's a segment before and after that need recalculation
      const prevStopId = stopConfigs[index - 1].stopId;
      const nextStopId = stopConfigs[index + 1].stopId;
      const newSegmentKey = `${prevStopId}-${nextStopId}`;
      changedSegmentKeys.push(newSegmentKey);
    } else if (index === 0 && stopConfigs.length > 1) {
      // Removed first stop - the new first segment needs recalculation
      const newFirstStopId = stopConfigs[1].stopId;
      const newSecondStopId = stopConfigs[2]?.stopId;
      if (newSecondStopId) {
        changedSegmentKeys.push(`${newFirstStopId}-${newSecondStopId}`);
      }
    } else if (index === stopConfigs.length - 1 && stopConfigs.length > 1) {
      // Removed last stop - the new last segment needs recalculation
      const newLastStopId = stopConfigs[stopConfigs.length - 2].stopId;
      const prevStopId = stopConfigs[stopConfigs.length - 2].stopId;
      const prevPrevStopId = stopConfigs[stopConfigs.length - 3]?.stopId;
      if (prevPrevStopId) {
        changedSegmentKeys.push(`${prevPrevStopId}-${prevStopId}`);
      }
    }
    
    setSegments(updatedSegments);
    
    // Auto-calculate changed segments
    if (changedSegmentKeys.length > 0) {
      handleCalculateChangedSegments(changedSegmentKeys);
    }
    
    // If circulator route, calculate return segment after stop is removed
    if (isCirculator && newStopConfigs.length >= 2) {
      setTimeout(() => {
        calculateReturnSegment();
      }, 200); // Delay slightly to ensure state is updated
    }
    
    // If editing an existing route, the map will recalculate geometry when route updates
    // The notification will be cleared by useFixedRoutesSync when geometry calculation completes
    if (selectedRoute) {
      // Trigger route update to clear geometry cache and force recalculation
      const routeWithClearedGeometry: FixedRoute = {
        ...selectedRoute,
        stops: newStopConfigs,
        segments: updatedSegments,
        waypoints: undefined,
        waypointStopIndices: undefined,
        geometry: undefined, // Clear geometry to force recalculation
      };
      updateRoute(routeWithClearedGeometry);
    }
    
    // Save to history after a short delay (debounced)
    setTimeout(() => saveToHistory(), 100);
  };

  const handleUpdateWaitTime = (index: number, waitTimeMinutes: number) => {
    const updatedStops = [...stopConfigs];
    updatedStops[index] = {
      ...updatedStops[index],
      waitTimeMinutes,
    };
    setStopConfigs(updatedStops);
    // Save to history after a short delay (debounced)
    setTimeout(() => saveToHistory(), 100);
  };

  const handleUpdateTravelTime = (fromStopId: string, toStopId: string, travelTimeMinutes: number) => {
    const updatedSegments = [...segments];
    const existingIndex = updatedSegments.findIndex(
      (seg) => seg.fromStopId === fromStopId && seg.toStopId === toStopId
    );
    
    if (existingIndex >= 0) {
      updatedSegments[existingIndex] = {
        ...updatedSegments[existingIndex],
        travelTimeMinutes,
      };
    } else {
      updatedSegments.push({
        fromStopId,
        toStopId,
        travelTimeMinutes,
      });
    }
    
    // Mark this segment as manually edited
    setManuallyEditedSegments(new Set(manuallyEditedSegments).add(`${fromStopId}-${toStopId}`));
    
    setSegments(updatedSegments);
    // Save to history after a short delay (debounced)
    setTimeout(() => saveToHistory(), 100);
  };

  const handleReorder = async (reorderedStops: FixedRouteStopConfig[]) => {
    const oldStopIds = stopConfigs.map(s => s.stopId);
    const newStopIds = reorderedStops.map(s => s.stopId);
    
    // If this is an existing route being edited, show rerendering notification
    if (selectedRoute) {
      setRouteGenerationStatus({ isGenerating: true, routeName: selectedRoute.name });
    }
    
    // Find which segments changed by comparing old and new orders
    const changedSegments: Array<{ fromStopId: string; toStopId: string; index: number }> = [];
    
    // Check each segment in the new order
    for (let i = 0; i < reorderedStops.length - 1; i++) {
      const fromStopId = reorderedStops[i].stopId;
      const toStopId = reorderedStops[i + 1].stopId;
      
      // Check if this segment existed in the old order
      const oldIndex = oldStopIds.indexOf(fromStopId);
      const oldNextIndex = oldIndex >= 0 && oldIndex < oldStopIds.length - 1 ? oldIndex + 1 : -1;
      const oldToStopId = oldNextIndex >= 0 ? oldStopIds[oldNextIndex] : null;
      
      // If segment is new or changed, mark it for recalculation
      if (oldToStopId !== toStopId) {
        changedSegments.push({ fromStopId, toStopId, index: i });
      }
    }
    
    // Update stop configs
    setStopConfigs(reorderedStops);
    
    // Rebuild segments array to match new stop order
    // Keep existing travel times if segment matches, otherwise use default
    const newSegments: FixedRouteSegment[] = [];
    for (let i = 0; i < reorderedStops.length - 1; i++) {
      const fromStopId = reorderedStops[i].stopId;
      const toStopId = reorderedStops[i + 1].stopId;
      
      // Try to find existing segment with same from/to
      const existingSegment = segments.find(
        (seg) => seg.fromStopId === fromStopId && seg.toStopId === toStopId
      );
      
      if (existingSegment) {
        newSegments.push(existingSegment);
      } else {
        // Use default travel time if segment doesn't exist
        newSegments.push({
          fromStopId,
          toStopId,
          travelTimeMinutes: 5, // Default fallback
        });
      }
    }
    
    setSegments(newSegments);
    
    // Auto-calculate only changed segments if there are any
    if (changedSegments.length > 0 && changedSegments.length <= reorderedStops.length) {
      // Only auto-calculate if reasonable number of changes (not a complete reorder)
      const changedSegmentKeys = changedSegments.map(s => `${s.fromStopId}-${s.toStopId}`);
      await handleCalculateChangedSegments(changedSegmentKeys);
    }
    
    // If editing an existing route, trigger route update to clear geometry cache and force recalculation
    if (selectedRoute) {
      const routeWithClearedGeometry: FixedRoute = {
        ...selectedRoute,
        stops: reorderedStops,
        segments: newSegments,
        waypoints: undefined,
        waypointStopIndices: undefined,
        geometry: undefined, // Clear geometry to force recalculation
      };
      updateRoute(routeWithClearedGeometry);
    }
    
    // Save to history after a short delay (debounced)
    setTimeout(() => saveToHistory(), 100);
  };

  // Reverse the stop order (flip route direction). Reuses handleReorder so segments are
  // rebuilt and geometry cleared for recalculation — reversed segments can differ (one-way streets).
  const handleReverseDirection = async () => {
    if (stopConfigs.length < 2) return;
    logEvent({ category: 'fixed_routes', action: 'reverse_direction', stop_count: stopConfigs.length });
    await handleReorder([...stopConfigs].reverse());
  };

  // F2: create an independently-editable opposite-direction (inbound) sibling route, linked to
  // this one. Operates on the saved route (kept current by autosave). Not for circulators.
  const handleCreateReturnRoute = () => {
    const source = selectedRoute ? (routes.find((r) => r.id === selectedRoute.id) ?? selectedRoute) : null;
    if (!source || source.isCirculator || source.stops.length < 2) return;
    // If a return route already exists, navigate to it instead of creating a duplicate.
    if (source.linkedRouteId && routes.some((r) => r.id === source.linkedRouteId)) {
      setSelectedRouteId(source.linkedRouteId);
      return;
    }
    const newId = uuidv4();
    const returnRoute = buildReversedRoute(source, newId);
    insertRoute(returnRoute, source.id);
    updateRoute({ ...source, directionId: 'outbound', linkedRouteId: newId });
    logEvent({ category: 'fixed_routes', action: 'create_return_route', stop_count: source.stops.length });
    setSelectedRouteId(newId);
  };

  const handleCalculateChangedSegments = async (segmentKeys?: string[]) => {
    // Calculate only specific segments that changed
    if (!segmentKeys || segmentKeys.length === 0) {
      return;
    }

    setIsCalculating(true);
    setCalculationProgress('');
    setCalculationError(null);
    setFailedSegments([]);

    const updatedSegments = [...segments];
    const failedSegmentNumbers: number[] = [];

    const segmentsToCalculate: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop; fromStopId: string; toStopId: string; segmentNumber: number }> = [];
    const segmentsFromCache: Array<{ fromStopId: string; toStopId: string; segmentNumber: number; distanceMeters: number; travelTimeMinutes: number }> = [];

    // Find segments to calculate - use current stopConfigs
    for (let i = 0; i < stopConfigs.length - 1; i++) {
      const fromStopId = stopConfigs[i].stopId;
      const toStopId = stopConfigs[i + 1].stopId;
      const segmentKey = `${fromStopId}-${toStopId}`;
      
      // Only calculate if this segment is in the changed list and not manually edited
      if (segmentKeys.includes(segmentKey) && !manuallyEditedSegments.has(segmentKey)) {
        const fromStop = stops.find((s) => s.id === fromStopId);
        const toStop = stops.find((s) => s.id === toStopId);
        
        if (fromStop && toStop && fromStop.coordinates && toStop.coordinates) {
          // Check cache first
          const cacheKey = getCacheKey(fromStopId, toStopId);
          const cached = distanceTimeCacheRef.current.get(cacheKey);
          
          if (cached) {
            segmentsFromCache.push({
              fromStopId,
              toStopId,
              segmentNumber: i + 1,
              distanceMeters: cached.distanceMeters,
              travelTimeMinutes: cached.travelTimeMinutes,
            });
          } else {
            segmentsToCalculate.push({ fromStop, toStop, fromStopId, toStopId, segmentNumber: i + 1 });
          }
        }
      }
    }

    // Apply cached segments immediately
    if (segmentsFromCache.length > 0) {
      segmentsFromCache.forEach(({ fromStopId, toStopId, distanceMeters, travelTimeMinutes }) => {
        const newSegment: FixedRouteSegment = {
          fromStopId,
          toStopId,
          travelTimeMinutes,
          distanceMeters,
        };
        
        const segIndex = updatedSegments.findIndex(
          (seg) => seg.fromStopId === fromStopId && seg.toStopId === toStopId
        );
        if (segIndex >= 0) {
          updatedSegments[segIndex] = newSegment;
        } else {
          updatedSegments.push(newSegment);
        }
        
        setSegments([...updatedSegments]);
      });
    }

    if (segmentsToCalculate.length === 0) {
      setIsCalculating(false);
      return;
    }

    setCalculationProgress(t('routeBuilder.calculation.calculatingChanged', { count: segmentsToCalculate.length }));

    try {
      const batchResults = await calculateTravelTimesAndDistancesBatch(
        segmentsToCalculate.map(({ fromStop, toStop }) => ({ fromStop, toStop }))
      );

      // Update segments with batch results and cache them
      batchResults.forEach((result, index) => {
        const segmentInfo = segmentsToCalculate[index];
        
        // Store in cache
        const cacheKey = getCacheKey(segmentInfo.fromStopId, segmentInfo.toStopId);
        distanceTimeCacheRef.current.set(cacheKey, result);
        
        const newSegment: FixedRouteSegment = {
          fromStopId: segmentInfo.fromStopId,
          toStopId: segmentInfo.toStopId,
          travelTimeMinutes: result.travelTimeMinutes,
          distanceMeters: result.distanceMeters,
        };
        
        const segIndex = updatedSegments.findIndex(
          (seg) => seg.fromStopId === segmentInfo.fromStopId && seg.toStopId === segmentInfo.toStopId
        );
        if (segIndex >= 0) {
          updatedSegments[segIndex] = newSegment;
        } else {
          updatedSegments.push(newSegment);
        }
        
        setSegments([...updatedSegments]);
      });
    } catch (error) {
      console.error('[RouteBuilder] Batch calculation failed for changed segments:', error);
      // Silent fail for auto-calculations - user can manually trigger if needed
    }

    setIsCalculating(false);
    setCalculationProgress('');
  };

  /**
   * Get cache key for a stop pair
   */
  const getCacheKey = (fromStopId: string, toStopId: string): string => {
    return `${fromStopId}-${toStopId}`;
  };

  /**
   * Get distance and time from cache or calculate if not cached
   */
  const getDistanceAndTime = async (
    fromStop: FixedRouteStop,
    toStop: FixedRouteStop
  ): Promise<{ distanceMeters: number; travelTimeMinutes: number }> => {
    const cacheKey = getCacheKey(fromStop.id, toStop.id);
    const cached = distanceTimeCacheRef.current.get(cacheKey);
    
    if (cached) {
      return cached;
    }

    // Calculate and cache
    const result = await calculateTravelTimeAndDistance(fromStop, toStop);
    distanceTimeCacheRef.current.set(cacheKey, result);
    return result;
  };

  /**
   * Calculate travel time and distance - uses HERE for bus routes, OSRM for regular routes
   */
  const calculateTravelTimeAndDistance = async (
    fromStop: FixedRouteStop,
    toStop: FixedRouteStop
  ): Promise<{ travelTimeMinutes: number; distanceMeters: number }> => {
    if (isBusRoute) {
      // Use HERE for bus routes
      return await hereCalculateTravelTimeAndDistance(fromStop, toStop, true);
    } else {
      // Use OSRM for regular routes
      return await osrmCalculateTravelTimeAndDistance(fromStop, toStop, 'driving');
    }
  };

  /**
   * Calculate travel times and distances in batch - uses HERE for bus routes, OSRM for regular routes
   */
  const calculateTravelTimesAndDistancesBatch = async (
    segments: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop }>
  ): Promise<Array<{ travelTimeMinutes: number; distanceMeters: number }>> => {
    if (isBusRoute) {
      // Use HERE for bus routes
      return await hereCalculateTravelTimesAndDistancesBatch(segments, true);
    } else {
      // Use OSRM for regular routes
      return await osrmCalculateTravelTimesAndDistancesBatch(segments, 'driving');
    }
  };

  /**
   * Calculate return segment for circulator routes (from last stop to first stop)
   * This is called automatically when stops change or circulator is enabled
   */
  const calculateReturnSegment = async (options?: {
    circulatorActive?: boolean;
    applyState?: boolean;
  }): Promise<FixedRouteSegment[] | null> => {
    const circulatorActive = options?.circulatorActive ?? isCirculator;
    const applyState = options?.applyState ?? true;
    if (!circulatorActive || stopConfigs.length < 2) {
      return null;
    }

    const lastStopId = stopConfigs[stopConfigs.length - 1].stopId;
    const firstStopId = stopConfigs[0].stopId;
    const lastStop = stops.find((s) => s.id === lastStopId);
    const firstStop = stops.find((s) => s.id === firstStopId);

    if (!lastStop || !firstStop || !lastStop.coordinates || !firstStop.coordinates) {
      console.warn('[RouteBuilder] Cannot calculate return segment: stops missing coordinates');
      return null;
    }

    try {
      // Remove existing return segment if it exists (we'll recalculate)
      const filteredSegments = segments.filter(
        (seg) => !(seg.fromStopId === lastStopId && seg.toStopId === firstStopId)
      );

      // Check cache first
      const cacheKey = getCacheKey(lastStopId, firstStopId);
      const cached = distanceTimeCacheRef.current.get(cacheKey);

      let returnTravelTime: number;
      let returnDistance: number;

      if (cached) {
        returnTravelTime = cached.travelTimeMinutes;
        returnDistance = cached.distanceMeters;
      } else {
        // Calculate using HERE for bus routes, OSRM for regular routes
        const result = await calculateTravelTimeAndDistance(lastStop, firstStop);
        returnTravelTime = result.travelTimeMinutes;
        returnDistance = result.distanceMeters;
        
        // Store in cache
        distanceTimeCacheRef.current.set(cacheKey, result);
      }

      const mergedSegments: FixedRouteSegment[] = [
        ...filteredSegments,
        {
          fromStopId: lastStopId,
          toStopId: firstStopId,
          travelTimeMinutes: returnTravelTime,
          distanceMeters: returnDistance,
        }
      ];
      if (applyState) {
        setSegments(mergedSegments);
      }

      console.log('[RouteBuilder] Calculated return segment:', {
        from: lastStop.name,
        to: firstStop.name,
        travelTimeMinutes: returnTravelTime,
        distanceMeters: returnDistance,
      });
      return mergedSegments;
    } catch (error) {
      console.error('[RouteBuilder] Failed to calculate return segment:', error);
      // Don't add a default segment - let the user know calculation failed
      // They can manually trigger calculation or fix the issue
      return null;
    }
  };

  /**
   * Optimize route order to minimize total distance traveled
   * Uses Nearest Neighbor heuristic for TSP optimization
   */
  const handleOptimizeRouteOrder = async () => {
    if (stopConfigs.length < 2) {
      alert(t('routeBuilder.alerts.minStopsForOptimize'));
      return;
    }

    // If this is an existing route being edited, show rerendering notification
    if (selectedRoute) {
      setRouteGenerationStatus({ isGenerating: true, routeName: selectedRoute.name });
    }

    console.log('[RouteBuilder] Starting route optimization...', { stopCount: stopConfigs.length });
    setIsOptimizing(true);
    setCalculationProgress(t('routeBuilder.optimization.calculatingDistances'));
    setCalculationError(null);

    try {
      // Get all stops with coordinates
      const routeStops = stopConfigs
        .map((stopConfig) => stops.find((s) => s.id === stopConfig.stopId))
        .filter((stop): stop is FixedRouteStop => stop !== undefined && stop.coordinates !== undefined);

      if (routeStops.length !== stopConfigs.length) {
        throw new Error('Some stops are missing coordinates');
      }

      if (routeStops.length < 2) {
        throw new Error('Need at least 2 stops with coordinates');
      }

      // Create all possible pairs for distance matrix
      const allPairs: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop }> = [];
      const pairsToCalculate: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop; fromIndex: number; toIndex: number }> = [];
      
      for (let i = 0; i < routeStops.length; i++) {
        for (let j = 0; j < routeStops.length; j++) {
          if (i !== j) {
            allPairs.push({ fromStop: routeStops[i], toStop: routeStops[j] });
            const cacheKey = getCacheKey(routeStops[i].id, routeStops[j].id);
            if (!distanceTimeCacheRef.current.has(cacheKey)) {
              pairsToCalculate.push({ fromStop: routeStops[i], toStop: routeStops[j], fromIndex: i, toIndex: j });
            }
          }
        }
      }

      const cachedCount = allPairs.length - pairsToCalculate.length;
      const progressMsg = cachedCount > 0
        ? t('routeBuilder.optimization.usingCached', { cached: cachedCount, toCalculate: pairsToCalculate.length })
        : t('routeBuilder.optimization.calculatingDistancesCount', { count: pairsToCalculate.length });
      setCalculationProgress(progressMsg);
      console.log('[RouteBuilder] Optimization progress:', progressMsg);

      // Get distances for pairs not in cache
      if (pairsToCalculate.length > 0) {
        const distanceResults = await calculateTravelTimesAndDistancesBatch(
          pairsToCalculate.map((p) => ({ fromStop: p.fromStop, toStop: p.toStop }))
        );

        // Store results in cache
        distanceResults.forEach((result, index) => {
          const pair = pairsToCalculate[index];
          const cacheKey = getCacheKey(pair.fromStop.id, pair.toStop.id);
          distanceTimeCacheRef.current.set(cacheKey, result);
        });
      }

      // Build distance matrix: distances[fromIndex][toIndex] = distance in meters
      const distances: number[][] = [];
      for (let i = 0; i < routeStops.length; i++) {
        distances[i] = [];
        for (let j = 0; j < routeStops.length; j++) {
          if (i === j) {
            distances[i][j] = 0;
          } else {
            const cacheKey = getCacheKey(routeStops[i].id, routeStops[j].id);
            const cached = distanceTimeCacheRef.current.get(cacheKey);
            distances[i][j] = cached?.distanceMeters || 0;
          }
        }
      }

      setCalculationProgress(t('routeBuilder.optimization.findingOptimal'));
      console.log('[RouteBuilder] Distance matrix built, starting TSP optimization...');

      // Improved Nearest Neighbor TSP heuristic
      // Try starting from each stop and pick the best overall solution
      let bestOrder: number[] = [];
      let bestDistance = Infinity;

      // Try each stop as a starting point
      for (let startIndex = 0; startIndex < routeStops.length; startIndex++) {
        const candidateOrder: number[] = [startIndex];
        const visited = new Set<number>([startIndex]);
        let currentIndex = startIndex;

        // Build route using nearest neighbor from this starting point
        for (let i = 1; i < routeStops.length; i++) {
          let nearestIndex = -1;
          let nearestDistance = Infinity;

          // Find nearest unvisited stop
          for (let j = 0; j < routeStops.length; j++) {
            if (!visited.has(j) && distances[currentIndex][j] < nearestDistance) {
              nearestDistance = distances[currentIndex][j];
              nearestIndex = j;
            }
          }

          if (nearestIndex >= 0) {
            candidateOrder.push(nearestIndex);
            visited.add(nearestIndex);
            currentIndex = nearestIndex;
          }
        }

        // Calculate total distance for this candidate route
        const candidateDistance = candidateOrder.reduce((total, index, i) => {
          if (i < candidateOrder.length - 1) {
            const nextIndex = candidateOrder[i + 1];
            return total + distances[index][nextIndex];
          }
          return total;
        }, 0);

        // Keep track of the best route found so far
        if (candidateDistance < bestDistance) {
          bestDistance = candidateDistance;
          bestOrder = [...candidateOrder];
        }
      }

      // If we want to keep the first stop fixed, find the best route that starts with stop 0
      // Otherwise use the globally best route
      const keepFirstStopFixed = true; // Set to false to allow any starting stop
      let optimizedOrder = keepFirstStopFixed 
        ? (() => {
            // Start from first stop but use the improved nearest neighbor
            const fixedStartOrder: number[] = [0];
            const fixedVisited = new Set<number>([0]);
            let currentIdx = 0;

            for (let i = 1; i < routeStops.length; i++) {
              let nearestIdx = -1;
              let nearestDist = Infinity;

              for (let j = 0; j < routeStops.length; j++) {
                if (!fixedVisited.has(j) && distances[currentIdx][j] < nearestDist) {
                  nearestDist = distances[currentIdx][j];
                  nearestIdx = j;
                }
              }

              if (nearestIdx >= 0) {
                fixedStartOrder.push(nearestIdx);
                fixedVisited.add(nearestIdx);
                currentIdx = nearestIdx;
              }
            }

            // Check if keeping first stop fixed gives a better result than the global best
            const fixedStartDistance = fixedStartOrder.reduce((total, index, i) => {
              if (i < fixedStartOrder.length - 1) {
                return total + distances[index][fixedStartOrder[i + 1]];
              }
              return total;
            }, 0);

            // Use whichever is better: global best or fixed-start
            return fixedStartDistance < bestDistance ? fixedStartOrder : bestOrder;
          })()
        : bestOrder;

      // Apply 2-opt improvement to the route
      // This tries swapping edges to see if it reduces total distance
      const improveWith2Opt = (order: number[]): number[] => {
        let improvedOrder = [...order];
        let improved = true;
        let iterations = 0;
        const maxIterations = 100; // Prevent infinite loops

        while (improved && iterations < maxIterations) {
          improved = false;
          iterations++;

          for (let i = 0; i < improvedOrder.length - 2; i++) {
            for (let j = i + 2; j < improvedOrder.length; j++) {
              // Calculate current distance for edges (i, i+1) and (j, j+1)
              const currentDistance = 
                distances[improvedOrder[i]][improvedOrder[i + 1]] +
                (j + 1 < improvedOrder.length ? distances[improvedOrder[j]][improvedOrder[j + 1]] : 0);

              // Calculate distance if we reverse the segment between i+1 and j
              const newDistance = 
                distances[improvedOrder[i]][improvedOrder[j]] +
                (j + 1 < improvedOrder.length ? distances[improvedOrder[i + 1]][improvedOrder[j + 1]] : 0);

              // If reversing this segment improves the route, do it
              if (newDistance < currentDistance) {
                // Reverse segment from i+1 to j
                const reversed = improvedOrder.slice(i + 1, j + 1).reverse();
                improvedOrder = [
                  ...improvedOrder.slice(0, i + 1),
                  ...reversed,
                  ...improvedOrder.slice(j + 1)
                ];
                improved = true;
                break; // Restart the improvement process
              }
            }
            if (improved) break;
          }
        }

        return improvedOrder;
      };

      // Apply 2-opt improvement if we have more than 3 stops
      if (routeStops.length > 3) {
        optimizedOrder = improveWith2Opt(optimizedOrder);
      }

      // Reorder stops based on optimized order
      const optimizedStopConfigs = optimizedOrder.map((index) => stopConfigs[index]);

      // Calculate total distance savings
      // Original distance: sum of distances in current order
      let originalTotalDistance = 0;
      for (let i = 0; i < stopConfigs.length - 1; i++) {
        const currentStop = routeStops.find((rs) => rs.id === stopConfigs[i].stopId);
        const nextStop = routeStops.find((rs) => rs.id === stopConfigs[i + 1].stopId);
        if (currentStop && nextStop) {
          const currentIndex = routeStops.indexOf(currentStop);
          const nextIndex = routeStops.indexOf(nextStop);
          if (currentIndex >= 0 && nextIndex >= 0) {
            originalTotalDistance += distances[currentIndex][nextIndex];
          }
        }
      }

      const optimizedDistance = optimizedOrder.reduce((total, index, i) => {
        if (i < optimizedOrder.length - 1) {
          const nextIndex = optimizedOrder[i + 1];
          return total + distances[index][nextIndex];
        }
        return total;
      }, 0);

      const distanceSaved = originalTotalDistance - optimizedDistance;
      const savingsPercent = originalTotalDistance > 0 ? ((distanceSaved / originalTotalDistance) * 100).toFixed(1) : '0';

      // Get stop names for display
      const originalOrderNames = stopConfigs.map((sc) => {
        const stop = stops.find((s) => s.id === sc.stopId);
        return stop?.name || sc.stopId;
      });
      const optimizedOrderNames = optimizedStopConfigs.map((sc) => {
        const stop = stops.find((s) => s.id === sc.stopId);
        return stop?.name || sc.stopId;
      });

      console.log('[RouteBuilder] Optimization complete!', {
        originalDistance: originalTotalDistance,
        optimizedDistance: optimizedDistance,
        distanceSaved: distanceSaved,
        savingsPercent: savingsPercent,
      });

      // Only apply optimization if it actually improves the distance
      if (distanceSaved <= 0) {
        setCalculationProgress('');
        setCalculationError(t('routeBuilder.optimization.alreadyOptimalError', { optimized: (optimizedDistance / 1609.34).toFixed(2), original: (originalTotalDistance / 1609.34).toFixed(2) }));
        setOptimizationResult(null);
        return;
      }

      setCalculationProgress(t('routeBuilder.optimization.complete'));

      // Store optimization result for modal BEFORE applying reorder
      // This ensures the modal shows immediately when optimization completes
      const result = {
        originalDistance: originalTotalDistance,
        optimizedDistance: optimizedDistance,
        distanceSaved: distanceSaved,
        savingsPercent: savingsPercent,
        originalOrder: originalOrderNames,
        optimizedOrder: optimizedOrderNames,
      };
      
      // Set optimization result immediately to show modal
      setOptimizationResult(result);

      // Apply optimized order and then automatically calculate all travel times
      try {
        await handleReorder(optimizedStopConfigs);
        // Wait a moment for state to update after reorder
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Automatically calculate all travel times after reordering
        console.log('[RouteBuilder] Optimization complete, calculating travel times...');
        const calculatedSegments = await handleCalculateAllTravelTimes();
        
        // Update the route with the newly calculated segments
        if (selectedRoute && calculatedSegments && calculatedSegments.length > 0) {
          const routeWithCalculatedSegments: FixedRoute = {
            ...selectedRoute,
            stops: optimizedStopConfigs,
            segments: calculatedSegments,
            geometry: undefined, // Clear geometry to force recalculation
          };
          updateRoute(routeWithCalculatedSegments);
          console.log('[RouteBuilder] Route updated with calculated travel times', calculatedSegments);
        } else {
          console.warn('[RouteBuilder] No calculated segments returned or route not selected');
        }
      } catch (error) {
        console.error('[RouteBuilder] Error applying optimized order:', error);
      }

      // Clear progress message after a short delay
      setTimeout(() => {
        setCalculationProgress('');
      }, 500);
    } catch (error) {
      console.error('[RouteBuilder] Route optimization error:', error);
      const errorMessage = error instanceof Error ? error.message : t('common.error');
      setCalculationError(t('routeBuilder.optimization.failed', { message: errorMessage }));
      setCalculationProgress('');
      setOptimizationResult(null);
    } finally {
      setIsOptimizing(false);
    }
  };

  const handleCalculateAllTravelTimes = async (): Promise<FixedRouteSegment[]> => {
    console.log('[RouteBuilder] handleCalculateAllTravelTimes called', { stopConfigs: stopConfigs.length, stops: stops.length });
    
    if (stopConfigs.length < 2) {
      alert(t('routeBuilder.alerts.minStopsForCalculate'));
      return segments; // Return current segments if validation fails
    }

    setIsCalculating(true);
    setCalculationProgress('');
    setCalculationError(null);
    setFailedSegments([]);

    // Note: Rate limiting already implemented below (task 1.6)

    // Start with empty array and build segments based on current stopConfigs order
    // This ensures we're working with the correct stop order after optimization
    let updatedSegments: FixedRouteSegment[] = [];
    const totalSegments = stopConfigs.length - 1;
    const failedSegmentNumbers: number[] = [];

    console.log('[RouteBuilder] Starting calculation for', totalSegments, 'segments', { stopConfigs: stopConfigs.map(s => s.stopId) });

    // Helper function to update a segment in the array
    const updateSegment = (newSegment: FixedRouteSegment) => {
      const index = updatedSegments.findIndex(
        (seg) => seg.fromStopId === newSegment.fromStopId && seg.toStopId === newSegment.toStopId
      );
      if (index >= 0) {
        updatedSegments[index] = newSegment;
      } else {
        updatedSegments.push(newSegment);
      }
      // Update state immediately to show progress
      setSegments([...updatedSegments]);
    };

    // Prepare segments for batch calculation
    const segmentsToCalculate: Array<{ fromStop: FixedRouteStop; toStop: FixedRouteStop; fromStopId: string; toStopId: string; segmentNumber: number }> = [];
    const segmentsFromCache: Array<{ fromStopId: string; toStopId: string; segmentNumber: number; distanceMeters: number; travelTimeMinutes: number }> = [];

    for (let i = 0; i < stopConfigs.length - 1; i++) {
      const segmentNumber = i + 1;
      const fromStopId = stopConfigs[i].stopId;
      const toStopId = stopConfigs[i + 1].stopId;
      const segmentKey = `${fromStopId}-${toStopId}`;

      // Skip manually edited segments
      if (manuallyEditedSegments.has(segmentKey)) {
        console.log(`[RouteBuilder] Skipping manually edited segment: ${segmentKey}`);
        continue;
      }

      const fromStop = stops.find((s) => s.id === fromStopId);
      const toStop = stops.find((s) => s.id === toStopId);

      if (!fromStop || !toStop) {
        const fromStopName = fromStop?.name || t('routeBuilder.defaults.unknownStop');
        const toStopName = toStop?.name || t('routeBuilder.defaults.unknownStop');
        failedSegmentNumbers.push(segmentNumber);
        console.warn(`[RouteBuilder] Stop not found for segment ${segmentNumber}: ${fromStopName} → ${toStopName}`);
        // Fallback to existing segment or default
        const existingSegment = segments.find(
          (s) => s.fromStopId === fromStopId && s.toStopId === toStopId
        );
        const fallbackSegment: FixedRouteSegment = {
          fromStopId,
          toStopId,
          travelTimeMinutes: existingSegment?.travelTimeMinutes || 5,
        };
        updateSegment(fallbackSegment);
        continue;
      }

      if (!fromStop.coordinates || !toStop.coordinates) {
        failedSegmentNumbers.push(segmentNumber);
        console.warn(`[RouteBuilder] Stop coordinates missing for segment ${segmentNumber}: ${fromStop.name} → ${toStop.name}`);
        // Use default fallback
        const fallbackSegment: FixedRouteSegment = {
          fromStopId,
          toStopId,
          travelTimeMinutes: 5,
        };
        updateSegment(fallbackSegment);
        continue;
      }

      // Check cache first
      const cacheKey = getCacheKey(fromStopId, toStopId);
      const cached = distanceTimeCacheRef.current.get(cacheKey);
      
      if (cached) {
        segmentsFromCache.push({
          fromStopId,
          toStopId,
          segmentNumber,
          distanceMeters: cached.distanceMeters,
          travelTimeMinutes: cached.travelTimeMinutes,
        });
      } else {
        segmentsToCalculate.push({ fromStop, toStop, fromStopId, toStopId, segmentNumber });
      }
    }

    // Apply cached segments immediately
    if (segmentsFromCache.length > 0) {
      console.log(`[RouteBuilder] Using ${segmentsFromCache.length} cached segments`);
      segmentsFromCache.forEach(({ fromStopId, toStopId, distanceMeters, travelTimeMinutes }) => {
        updateSegment({
          fromStopId,
          toStopId,
          travelTimeMinutes,
          distanceMeters,
        });
      });
    }

    // Calculate all segments in batch if we have any
    if (segmentsToCalculate.length > 0) {
      const cachedCount = segmentsFromCache.length;
      setCalculationProgress(
        cachedCount > 0
          ? t('routeBuilder.calculation.usingCachedAndCalculating', { cached: cachedCount, toCalculate: segmentsToCalculate.length })
          : t('routeBuilder.calculation.calculatingBatch', { count: segmentsToCalculate.length })
      );
      
      try {
        const batchResults = await calculateTravelTimesAndDistancesBatch(
          segmentsToCalculate.map(({ fromStop, toStop }) => ({ fromStop, toStop }))
        );

        // Update segments with batch results and cache them
        batchResults.forEach((result, index) => {
          const segmentInfo = segmentsToCalculate[index];
          
          // Store in cache
          const cacheKey = getCacheKey(segmentInfo.fromStopId, segmentInfo.toStopId);
          distanceTimeCacheRef.current.set(cacheKey, result);
          
          const newSegment: FixedRouteSegment = {
            fromStopId: segmentInfo.fromStopId,
            toStopId: segmentInfo.toStopId,
            travelTimeMinutes: result.travelTimeMinutes,
            distanceMeters: result.distanceMeters,
          };
          updateSegment(newSegment);
          
          setCalculationProgress(t('routeBuilder.calculation.calculatedSegment', { number: segmentInfo.segmentNumber, total: totalSegments, from: segmentInfo.fromStop.name, to: segmentInfo.toStop.name }));
        });
      } catch (error) {
        console.error('[RouteBuilder] Batch calculation failed, falling back to individual requests:', error);
        // Fallback to individual requests if batch fails
        for (const segmentInfo of segmentsToCalculate) {
          try {
            setCalculationProgress(t('routeBuilder.calculation.calculatingSegment', { number: segmentInfo.segmentNumber, total: totalSegments, from: segmentInfo.fromStop.name, to: segmentInfo.toStop.name }));
            const { travelTimeMinutes: travelTime, distanceMeters: distance } = await calculateTravelTimeAndDistance(segmentInfo.fromStop, segmentInfo.toStop);
            
            // Store in cache
            const cacheKey = getCacheKey(segmentInfo.fromStopId, segmentInfo.toStopId);
            distanceTimeCacheRef.current.set(cacheKey, { travelTimeMinutes: travelTime, distanceMeters: distance });
            
            const newSegment: FixedRouteSegment = {
              fromStopId: segmentInfo.fromStopId,
              toStopId: segmentInfo.toStopId,
              travelTimeMinutes: travelTime,
              distanceMeters: distance,
            };
            updateSegment(newSegment);

            // Rate limiting: wait 1 second between requests
            if (segmentsToCalculate.indexOf(segmentInfo) < segmentsToCalculate.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 1100));
            }
          } catch (error) {
            failedSegmentNumbers.push(segmentInfo.segmentNumber);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            console.error(`[RouteBuilder] Failed to calculate travel time for segment ${segmentInfo.segmentNumber} (${segmentInfo.fromStop.name} → ${segmentInfo.toStop.name}):`, error);
            
            // Fallback to existing segment or default
            const existingSegment = segments.find(
              (s) => s.fromStopId === segmentInfo.fromStopId && s.toStopId === segmentInfo.toStopId
            );
            const fallbackSegment: FixedRouteSegment = {
              fromStopId: segmentInfo.fromStopId,
              toStopId: segmentInfo.toStopId,
              travelTimeMinutes: existingSegment?.travelTimeMinutes || 5,
            };
            updateSegment(fallbackSegment);
          }
        }
      }
    }

    console.log('[RouteBuilder] Calculation complete', { updatedSegments, failedSegments: failedSegmentNumbers });

    // For circulator routes, ALWAYS calculate return segment from last stop to first stop
    if (isCirculator && stopConfigs.length >= 2) {
      const lastStopId = stopConfigs[stopConfigs.length - 1].stopId;
      const firstStopId = stopConfigs[0].stopId;
      const returnSegmentKey = `${lastStopId}-${firstStopId}`;
      const lastStop = stops.find((s) => s.id === lastStopId);
      const firstStop = stops.find((s) => s.id === firstStopId);

      // Check if return segment already exists
      const existingReturnSegment = updatedSegments.find(
        (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
      );

      // Always calculate return segment if circulator is enabled and stops have coordinates
      // (Even if manually edited, we'll recalculate to ensure it's up to date)
      if (lastStop && firstStop && lastStop.coordinates && firstStop.coordinates) {
        // Remove existing return segment if it exists (we'll recalculate)
        const filteredSegments = updatedSegments.filter(
          (seg) => !(seg.fromStopId === lastStopId && seg.toStopId === firstStopId)
        );
        updatedSegments = filteredSegments;
        setCalculationProgress(t('routeBuilder.calculation.calculatingReturn', { from: lastStop.name, to: firstStop.name }));
        
        // Check cache first
        const cacheKey = getCacheKey(lastStopId, firstStopId);
        const cached = distanceTimeCacheRef.current.get(cacheKey);
        
        if (cached) {
          const returnSegment: FixedRouteSegment = {
            fromStopId: lastStopId,
            toStopId: firstStopId,
            travelTimeMinutes: cached.travelTimeMinutes,
            distanceMeters: cached.distanceMeters,
          };
          updateSegment(returnSegment);
        } else {
          try {
            const { travelTimeMinutes: returnTravelTime, distanceMeters: returnDistance } = await calculateTravelTimeAndDistance(lastStop, firstStop);
            
            // Store in cache
            distanceTimeCacheRef.current.set(cacheKey, { travelTimeMinutes: returnTravelTime, distanceMeters: returnDistance });
            
            const returnSegment: FixedRouteSegment = {
              fromStopId: lastStopId,
              toStopId: firstStopId,
              travelTimeMinutes: returnTravelTime,
              distanceMeters: returnDistance,
            };
            updateSegment(returnSegment);
            console.log('[RouteBuilder] Calculated return segment:', returnSegment);
          } catch (error) {
            console.error('[RouteBuilder] Failed to calculate return segment:', error);
            // Fallback to existing segment or default
            const existingSegment = segments.find(
              (s) => s.fromStopId === lastStopId && s.toStopId === firstStopId
            );
            const fallbackSegment: FixedRouteSegment = {
              fromStopId: lastStopId,
              toStopId: firstStopId,
              travelTimeMinutes: existingSegment?.travelTimeMinutes || 5,
            };
            updateSegment(fallbackSegment);
            failedSegmentNumbers.push(totalSegments + 1); // Add return segment to failed list
          }
        }
      }
    }

    // Final update to ensure all segments are properly set
    setSegments(updatedSegments);
    setIsCalculating(false);
    setCalculationProgress('');

    // Show error summary if any segments failed
    if (failedSegmentNumbers.length > 0) {
      setFailedSegments(failedSegmentNumbers);
      const successCount = totalSegments - failedSegmentNumbers.length;
      const failedCount = failedSegmentNumbers.length;
      
      if (failedCount === totalSegments) {
        setCalculationError(t('routeBuilder.calculation.errorAll', { count: failedCount }));
      } else {
        setCalculationError(t('routeBuilder.calculation.errorPartial', { success: successCount, total: totalSegments, failed: failedSegmentNumbers.join(', ') }));
      }
    } else {
      setCalculationError(null);
      setFailedSegments([]);
    }
    
    // Return the calculated segments
    return updatedSegments;
  };

  const validateForm = (): boolean => {
    const errors: typeof validationErrors = {};
    let isValid = true;

    if (!routeName.trim()) {
      errors.routeName = t('routeBuilder.validation.routeNameRequired');
      isValid = false;
    }

    if (stopConfigs.length < 2) {
      isValid = false; // This will be handled separately
    }

    // Validate time format (HH:mm)
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(startTime)) {
      errors.startTime = t('routeBuilder.validation.startTimeFormat');
      isValid = false;
    }

    if (endTime && !timeRegex.test(endTime)) {
      errors.endTime = t('routeBuilder.validation.endTimeFormat');
      isValid = false;
    }

    // Circulator routes require an end time
    if (isCirculator && !endTime) {
      errors.endTime = t('routeBuilder.validation.circulatorEndTime');
      isValid = false;
    }

    // Validate traffic multiplier
    if (trafficMultiplier < MIN_TRAFFIC_MULTIPLIER || trafficMultiplier > MAX_TRAFFIC_MULTIPLIER) {
      errors.trafficMultiplier = t('routeBuilder.validation.trafficMultiplierRange', { min: MIN_TRAFFIC_MULTIPLIER, max: MAX_TRAFFIC_MULTIPLIER });
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleCancel = () => {
    cancelRouteCapture();
    circulatorReturnLegCacheRef.current = null;
    const idToRemove = draftRouteIdRef.current ?? draftRouteId;
    if (isCreating && idToRemove) {
      deleteRoute(idToRemove);
      clearDraftRouteTracking();
    }
    setIsEditing(false);
    setIsCreating(false);
    setSelectedRoute(null);
    setSelectedRouteId(null);
    setCapturePoints([]);
    setCaptureWaypoints([]);
    setCaptureWaypointStopIndices([]);
    // Note: vehicleCapacity stays at current value, will be reset when creating new route
  };

  // Extract save logic into reusable function
  const performSave = useCallback((autoSave: boolean = false) => {
    // Skip validation for auto-save if form is invalid (user is still editing)
    if (!autoSave && !validateForm()) {
      return false;
    }

    // Basic validation - must have at least 2 stops and valid route name
    if (stopConfigs.length < 2 || !routeName.trim()) {
      return false;
    }

    // Use existing segments if available, otherwise generate default segments
    let routeSegments = segments;
    if (routeSegments.length === 0) {
      // Generate default segments if none exist
      routeSegments = [];
      for (let i = 0; i < stopConfigs.length - 1; i++) {
        routeSegments.push({
          fromStopId: stopConfigs[i].stopId,
          toStopId: stopConfigs[i + 1].stopId,
          travelTimeMinutes: 5, // Default fallback
        });
      }
      
      // For circulator routes, return segment will be calculated by calculateReturnSegment()
      // Don't add a default here - let it be calculated properly
    } else if (isCirculator && stopConfigs.length >= 2) {
      // For circulator routes, return segment will be calculated by calculateReturnSegment()
      // Don't add a default here - let it be calculated properly
      // Calculate it now if stops have coordinates
      const lastStopId = stopConfigs[stopConfigs.length - 1].stopId;
      const firstStopId = stopConfigs[0].stopId;
      const lastStop = stops.find((s) => s.id === lastStopId);
      const firstStop = stops.find((s) => s.id === firstStopId);
      
      // Only add return segment if it doesn't exist and we can calculate it
      const existingReturnSegment = routeSegments.find(
        (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
      );
      
      if (!existingReturnSegment && lastStop && firstStop && lastStop.coordinates && firstStop.coordinates) {
        // Will be calculated by calculateReturnSegment() - don't add default
        // This ensures it's always calculated, not defaulted
      }
    }

    const selectedRouteFromStore = selectedRoute
      ? (routes.find((r) => r.id === selectedRoute.id) ?? selectedRoute)
      : null;

    // Check if stops have changed compared to the selected route
    const stopsChanged = selectedRouteFromStore && (
      selectedRouteFromStore.stops.length !== stopConfigs.length ||
      selectedRouteFromStore.stops.some((oldStop, idx) => oldStop.stopId !== stopConfigs[idx]?.stopId)
    );
    
    // Check if circulator status changed
    const circulatorChanged = selectedRouteFromStore && selectedRouteFromStore.isCirculator !== isCirculator;
    const waypointsChanged = selectedRouteFromStore && (
      (selectedRouteFromStore.waypoints?.length ?? 0) > 0 || (selectedRouteFromStore.waypointStopIndices?.length ?? 0) > 0
    );
    // Toggling freehand changes the rendered shape (straight vs road-following) -> recompute.
    const freehandChanged = selectedRouteFromStore && Boolean(selectedRouteFromStore.freehand) !== freehand;

    // Determine if route needs geometry recalculation
    const needsGeometryUpdate = stopsChanged || circulatorChanged || waypointsChanged || freehandChanged;
    
    const route: FixedRoute = {
      id: selectedRouteFromStore?.id || `route-${Date.now()}`,
      name: routeName.trim(),
      startTime,
      endTime: endTime || undefined,
      stops: stopConfigs,
      segments: routeSegments,
      trafficMultiplier: trafficMultiplier !== DEFAULT_TRAFFIC_MULTIPLIER ? trafficMultiplier : undefined,
      isCirculator: isCirculator,
      reverseReturnJourney,
      isBusRoute: isBusRoute,
      freehand: freehand || undefined,
      vehicleCount: vehicleCount && vehicleCount >= 1 ? vehicleCount : (isCirculator ? undefined : 1), // Default to 1 for non-circulator routes
      vehicleCapacity: vehicleCapacity && vehicleCapacity > 0 ? vehicleCapacity : 50, // Always save capacity, default to 50
      waypoints: undefined,
      waypointStopIndices: undefined,
      geometry: needsGeometryUpdate ? undefined : selectedRouteFromStore?.geometry,
      // Preserve display identity across saves/creates.
      color: selectedRouteFromStore?.color || generateRouteColor(selectedRouteFromStore?.id || `route-${Date.now()}`),
      width: selectedRouteFromStore?.width,
      hidden: selectedRouteFromStore?.hidden,
      lineId: selectedRouteFromStore?.lineId,
      layerId: selectedRouteFromStore?.layerId,
      isManuallyEdited: selectedRouteFromStore?.isManuallyEdited,
      // Preserve identity/GTFS metadata across saves; editable GTFS fields
      // (service days, headsign, description) come from the Route Details tab.
      shortName: selectedRouteFromStore?.shortName,
      serviceDays: serviceDays ?? selectedRouteFromStore?.serviceDays,
      serviceDateRange: selectedRouteFromStore?.serviceDateRange,
      serviceExceptions: selectedRouteFromStore?.serviceExceptions,
      tripHeadsign: tripHeadsign.trim() || selectedRouteFromStore?.tripHeadsign,
      routeDesc: routeDesc.trim() || selectedRouteFromStore?.routeDesc,
      gtfsRouteId: selectedRouteFromStore?.gtfsRouteId,
      gtfsRouteType: selectedRouteFromStore?.gtfsRouteType,
      routeTextColor: selectedRouteFromStore?.routeTextColor,
      gtfsAgency: selectedRouteFromStore?.gtfsAgency,
      tripSchedules: selectedRouteFromStore?.tripSchedules,
    };

    if (needsGeometryUpdate || !selectedRouteFromStore) {
      setRouteGenerationStatus({ isGenerating: true, routeName: route.name });
    }
    
    if (selectedRouteFromStore) {
      updateRoute(route);
      if (!needsGeometryUpdate) {
        setRouteGenerationStatus({ isGenerating: false, routeName: null });
      }
      return true;
    } else {
      // For new routes, only save manually (not auto-save)
      if (!autoSave) {
        addRoute(route);
      }
      return false;
    }
  }, [
    routeName, stopConfigs, segments, isCirculator, reverseReturnJourney, isBusRoute, startTime, endTime, trafficMultiplier, vehicleCount, vehicleCapacity,
    serviceDays, tripHeadsign, routeDesc,
    selectedRoute, updateRoute, addRoute, setRouteGenerationStatus, captureWaypoints, captureWaypointStopIndices
  ]);

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    if (stopConfigs.length < 2) {
      setValidationErrors({ ...validationErrors, routeName: t('routeBuilder.validation.minStops') });
      return;
    }

    finishRouteCapture();
    setValidationErrors({});
    setIsSaving(true);
    
    const saved = performSave(false);
    
    if (saved) {
      setIsSaving(false);
      setIsEditing(false);
      setIsCreating(false);
      setSelectedRoute(null);
      clearDraftRouteTracking();
      setCapturePoints([]);
      setCaptureWaypoints([]);
      setCaptureWaypointStopIndices([]);
    } else {
      setIsSaving(false);
      setIsEditing(false);
      setIsCreating(false);
      setSelectedRoute(null);
      clearDraftRouteTracking();
      setCapturePoints([]);
      setCaptureWaypoints([]);
      setCaptureWaypointStopIndices([]);
    }
  };

  const handleDeleteClick = (routeId: string) => {
    const route = routes.find((r) => r.id === routeId);
    if (route) {
      setRouteToDelete(route);
    }
  };

  const projectKeptOrphanStopsToMapLayer = useCallback((orphanStopIds: string[]) => {
    if (!appContext || orphanStopIds.length === 0) return;
    const global = appContext.global;
    const orphanStops = orphanStopIds
      .map((stopId) => stops.find((stop) => stop.id === stopId))
      .filter((stop): stop is FixedRouteStop => !!stop && hasValidStopCoordinates(stop));
    if (orphanStops.length === 0) return;

    const layerName = 'Fixed Route Orphaned Stops';
    const existingLayer = global.mapLayers.find((layer) => layer.layerName === layerName);
    const maxLayerId = global.mapLayers.length > 0
      ? Math.max(...global.mapLayers.map((layer) => layer.layerId))
      : 0;
    const layerId = existingLayer ? existingLayer.layerId : maxLayerId + 1;

    const newPoints: Point[] = orphanStops.map((stop) => ({
      id: `fixed-route-orphan-stop-${stop.id}`,
      layerId,
      type: 'point',
      name: stop.name || `Stop ${stop.id}`,
      description: stop.description || 'Retained from deleted fixed route',
      colour: '#1976d2',
      icon: 'bus',
      size: 24,
      isLocked: false,
      coordinates: [stop.coordinates.lat, stop.coordinates.lng],
      fixedRouteStopId: stop.id,
      fixedRouteStopMetadata: {
        type: 'fixedRouteStop',
        fixedRouteStopId: stop.id,
        uatStopId: stop.uatStopId,
        label: stop.label,
        address: stop.address,
        placeId: stop.placeId,
        uatMetadata: stop.uatMetadata,
      },
    } as Point));

    global.setLayers((prevLayers) => {
      const targetLayer = prevLayers.find((layer) => layer.layerName === layerName);
      if (!targetLayer) {
        const createdLayer: Layer = {
          layerId,
          layerName,
          hidden: false,
          expanded: true,
          shapes: newPoints,
        };
        return [...prevLayers, createdLayer];
      }

      const existingPointIds = new Set(
        targetLayer.shapes
          .filter((shape): shape is Point => shape.type === 'point')
          .map((point) => point.id)
      );
      const pointsToAppend = newPoints.filter((point) => !existingPointIds.has(point.id));
      if (pointsToAppend.length === 0) return prevLayers;

      return prevLayers.map((layer) =>
        layer.layerId === targetLayer.layerId
          ? { ...layer, shapes: [...layer.shapes, ...pointsToAppend] }
          : layer
      );
    });
    global.setActiveLayerId(layerId);
  }, [appContext, stops]);

  const finalizeRouteDeletion = useCallback((route: FixedRoute, orphanStopIds: string[], deleteOrphanedStops: boolean) => {
    deleteRoute(route.id);

    if (deleteOrphanedStops) {
      orphanStopIds.forEach((stopId) => deleteStop(stopId));
    } else {
      projectKeptOrphanStopsToMapLayer(orphanStopIds);
    }

    if (selectedRoute?.id === route.id) {
      setIsEditing(false);
      setIsCreating(false);
      setSelectedRoute(null);
    }
    if (selectedRouteId === route.id) {
      setSelectedRouteId(null);
    }
    setRouteToDelete(null);
    setOrphanedStopsDecision(null);
  }, [deleteRoute, deleteStop, projectKeptOrphanStopsToMapLayer, selectedRoute?.id, selectedRouteId, setSelectedRouteId]);

  const handleConfirmDelete = () => {
    if (routeToDelete) {
      const remainingRoutes = routes.filter((route) => route.id !== routeToDelete.id);
      const remainingUsedStopIds = new Set(
        remainingRoutes.flatMap((route) => route.stops.map((stopConfig) => stopConfig.stopId))
      );
      const candidateStopIds = routeToDelete.stops.map((stopConfig) => stopConfig.stopId);
      const orphanStopIds = candidateStopIds.filter((stopId) => !remainingUsedStopIds.has(stopId));

      if (orphanStopIds.length > 0) {
        setOrphanedStopsDecision({
          route: routeToDelete,
          orphanStopIds,
        });
        setRouteToDelete(null);
        return;
      }

      finalizeRouteDeletion(routeToDelete, orphanStopIds, false);
    }
  };

  // Auto-generate route name from first and last stop
  useEffect(() => {
    // Only auto-generate if:
    // 1. We're creating or editing
    // 2. User hasn't manually set the name (or name matches last auto-generated)
    // 3. We have at least 2 stops
    if ((isCreating || isEditing) && stopConfigs.length >= 2 && !routeNameManuallySet.current) {
      const firstStopName = stops.find((s) => s.id === stopConfigs[0].stopId)?.name || stopConfigs[0].stopId;
      const lastStopName = stops.find((s) => s.id === stopConfigs[stopConfigs.length - 1].stopId)?.name || stopConfigs[stopConfigs.length - 1].stopId;
      const autoName = `${firstStopName} → ${lastStopName}`;
      
      // Only update if it's different from current name
      if (autoName !== routeName && autoName !== lastAutoGeneratedName.current) {
        setRouteName(autoName);
        lastAutoGeneratedName.current = autoName;
      }
    } else if (stopConfigs.length < 2 && !routeNameManuallySet.current) {
      // Clear name if we don't have enough stops
      setRouteName('');
      lastAutoGeneratedName.current = '';
    }
  }, [stopConfigs, stops, isCreating, isEditing, routeName]);

  // Auto-save when editing an existing route (debounced)
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Only auto-save when editing (not creating) and route is valid
    if (!isEditing || isCreating || !selectedRoute) {
      return;
    }

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Debounce auto-save by 500ms
    autoSaveTimeoutRef.current = setTimeout(() => {
      performSave(true);
    }, 500);

    // Cleanup timeout on unmount or dependency change
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [
    routeName, stopConfigs, segments, isCirculator, startTime, endTime, trafficMultiplier,
    isEditing, isCreating, selectedRoute, performSave
  ]);

  const showEditor = isCreating || isEditing;
  const getStopName = (stopId: string) => stops.find((s) => s.id === stopId)?.name || stopId;
  const availableStops = stops.filter((s) => !stopConfigs.find((sc) => sc.stopId === s.id));

  const layerStopOptions = useMemo(() => {
    if (!appContext?.global.mapLayers) {
      return [];
    }

    const routeStopIds = new Set(stopConfigs.map((config) => config.stopId));

    return appContext.global.mapLayers
      .map((layer) => {
        const addablePointCount = layer.shapes.filter((shape) => {
          if (shape.type !== 'point') {
            return false;
          }
          const existingStop = stops.find((stop) => stop.mapPointId === shape.id);
          return !existingStop || !routeStopIds.has(existingStop.id);
        }).length;

        return {
          layerId: layer.layerId,
          layerName: layer.layerName,
          count: addablePointCount,
        };
      })
      .filter((option) => option.count > 0);
  }, [appContext?.global.mapLayers, stops, stopConfigs]);

  // Optimization Result Modal - Rendered outside main content so it always shows
  const optimizationModal = (
    <Dialog
      open={!!optimizationResult}
      onClose={() => setOptimizationResult(null)}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={false}
      disableAutoFocus={false}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          zIndex: 9999, // Ensure it's above everything else
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 9998,
          },
        },
      }}
    >
      <DialogContent sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          {optimizationResult && (() => {
            const absDistanceSaved = Math.abs(optimizationResult.distanceSaved);
            const absSavingsPercent = Math.abs(parseFloat(optimizationResult.savingsPercent));
            const isNoChange = absDistanceSaved < 0.1 && absSavingsPercent < 0.1;
            const isSameOrder = optimizationResult.originalOrder.length === optimizationResult.optimizedOrder.length &&
              optimizationResult.originalOrder.every((stop, idx) => stop === optimizationResult.optimizedOrder[idx]);
            const isAlreadyOptimal = isNoChange && isSameOrder;
            
            return (
              <>
                <Icon size={20} name="circle-check" style={{
                    fontSize: 64,
                    color: isAlreadyOptimal ? '#0288d1' : '#2e7d32',
                    marginBottom: 16,
                  }} />
                <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                  {isAlreadyOptimal ? t('routeBuilder.optimization.alreadyOptimalTitle') : t('routeBuilder.optimization.successTitle')}
                </Typography>
              </>
            );
          })()}
          {!optimizationResult && (
            <>
              <Icon size={20} name="circle-check" style={{
                  fontSize: 64,
                  color: '#2e7d32',
                  marginBottom: 16,
                }} />
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                {t('routeBuilder.optimization.successTitle')}
              </Typography>
            </>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {optimizationResult && (() => {
              const absDistanceSaved = Math.abs(optimizationResult.distanceSaved);
              const absSavingsPercent = Math.abs(parseFloat(optimizationResult.savingsPercent));
              const isNoChange = absDistanceSaved < 0.1 && absSavingsPercent < 0.1;
              const isSameOrder = optimizationResult.originalOrder.length === optimizationResult.optimizedOrder.length &&
                optimizationResult.originalOrder.every((stop, idx) => stop === optimizationResult.optimizedOrder[idx]);
              return (isNoChange && isSameOrder)
                ? t('routeBuilder.optimization.subtitleAlreadyOptimal')
                : t('routeBuilder.optimization.subtitleSuccess');
            })()}
          </Typography>
        </Box>

        {/* Savings Highlight */}
        {optimizationResult && (() => {
          const absDistanceSaved = Math.abs(optimizationResult.distanceSaved);
          const absSavingsPercent = Math.abs(parseFloat(optimizationResult.savingsPercent));
          const isNoChange = absDistanceSaved < 0.1 && absSavingsPercent < 0.1; // Less than 0.1ft or 0.1% change
          const isSameOrder = optimizationResult.originalOrder.length === optimizationResult.optimizedOrder.length &&
            optimizationResult.originalOrder.every((stop, idx) => stop === optimizationResult.optimizedOrder[idx]);
          const isAlreadyOptimal = isNoChange && isSameOrder;
          const isImproved = optimizationResult.distanceSaved > 0 && !isAlreadyOptimal;
          
          if (isAlreadyOptimal) {
            return (
              <Box
                sx={{
                  bgcolor: 'info.light',
                  color: 'info.contrastText',
                  borderRadius: 2,
                  p: 3,
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                }}
              >
                <Icon size={40} name="circle-check" />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {t('routeBuilder.optimization.alreadyOptimalBoxTitle')}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {t('routeBuilder.optimization.alreadyOptimalBoxBody')}
                  </Typography>
                </Box>
              </Box>
            );
          }
          
          return (
            <Box
              sx={{
                bgcolor: isImproved ? 'success.light' : 'warning.light',
                color: isImproved ? 'success.contrastText' : 'warning.contrastText',
                borderRadius: 2,
                p: 3,
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              {isImproved ? (
                <Icon size={40} d={ICON_FALLBACKS.TrendingDown} />
              ) : (
                <Icon size={40} d={ICON_FALLBACKS.TrendingUp} />
              )}
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {isImproved
                    ? t('routeBuilder.optimization.saved', { distance: formatDistance(absDistanceSaved, distanceUnit), percent: absSavingsPercent.toFixed(1) })
                    : t('routeBuilder.optimization.increased', { distance: formatDistance(absDistanceSaved, distanceUnit), percent: absSavingsPercent.toFixed(1) })}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {isImproved
                    ? t('routeBuilder.optimization.distanceReduced', { from: formatDistance(optimizationResult.originalDistance, distanceUnit), to: formatDistance(optimizationResult.optimizedDistance, distanceUnit) })
                    : t('routeBuilder.optimization.distanceIncreased', { from: formatDistance(optimizationResult.originalDistance, distanceUnit), to: formatDistance(optimizationResult.optimizedDistance, distanceUnit) })}
                </Typography>
              </Box>
            </Box>
          );
        })()}

        {/* Route Comparison */}
        {optimizationResult && (() => {
          const absDistanceSaved = Math.abs(optimizationResult.distanceSaved);
          const absSavingsPercent = Math.abs(parseFloat(optimizationResult.savingsPercent));
          const isNoChange = absDistanceSaved < 0.1 && absSavingsPercent < 0.1;
          const isSameOrder = optimizationResult.originalOrder.length === optimizationResult.optimizedOrder.length &&
            optimizationResult.originalOrder.every((stop, idx) => stop === optimizationResult.optimizedOrder[idx]);
          const isAlreadyOptimal = isNoChange && isSameOrder;

          // Create mapping: originalIndex -> optimizedIndex
          const originalToOptimized: Map<number, number> = new Map();
          optimizationResult.originalOrder.forEach((stopName, originalIndex) => {
            const optimizedIndex = optimizationResult.optimizedOrder.findIndex(name => name === stopName);
            if (optimizedIndex !== -1) {
              originalToOptimized.set(originalIndex, optimizedIndex);
            }
          });

          // Create mapping: optimizedIndex -> originalIndex
          const optimizedToOriginal: Map<number, number> = new Map();
          optimizationResult.optimizedOrder.forEach((stopName, optimizedIndex) => {
            const originalIndex = optimizationResult.originalOrder.findIndex(name => name === stopName);
            if (originalIndex !== -1) {
              optimizedToOriginal.set(optimizedIndex, originalIndex);
            }
          });

          // Generate distinct colors for each stop
          const generateStopColor = (index: number, total: number): string => {
            const hue = (index * 360 / total) % 360;
            const saturation = 65;
            const lightness = 55;
            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          };

          const stopColors = optimizationResult.originalOrder.map((_, idx) => 
            generateStopColor(idx, optimizationResult.originalOrder.length)
          );

          // Component for arrows that measures actual chip positions
          const ArrowConnector = () => {
            const [arrowPaths, setArrowPaths] = useState<Array<{d: string; color: string; markerId: string}>>([]);
            const arrowsContainerRef = useRef<HTMLDivElement>(null);

            const calculatePaths = () => {
              if (!arrowsContainerRef.current) return;

              const containerRect = arrowsContainerRef.current.getBoundingClientRect();
              const paths: Array<{d: string; color: string; markerId: string}> = [];

              optimizationResult.originalOrder.forEach((_, originalIndex) => {
                const optimizedIndex = originalToOptimized.get(originalIndex);
                const moved = optimizedIndex !== undefined && optimizedIndex !== originalIndex;
                
                if (!moved || !originalChipRefs.current[originalIndex] || !optimizedChipRefs.current[optimizedIndex!]) return;

                const originalChip = originalChipRefs.current[originalIndex] as HTMLDivElement;
                const optimizedChip = optimizedChipRefs.current[optimizedIndex!] as HTMLDivElement;

                const originalRect = originalChip.getBoundingClientRect();
                const optimizedRect = optimizedChip.getBoundingClientRect();

                // Calculate relative positions within the arrows container
                const startX = originalRect.right - containerRect.left;
                const startY = originalRect.top + originalRect.height / 2 - containerRect.top;
                const endX = optimizedRect.left - containerRect.left;
                const endY = optimizedRect.top + optimizedRect.height / 2 - containerRect.top;

                const midX = (startX + endX) / 2;
                const controlY = (startY + endY) / 2;

                // Create smooth curved path
                const pathD = `M ${startX} ${startY} Q ${midX} ${startY} ${midX} ${controlY} T ${endX} ${endY}`;
                const markerId = `arrowhead-${originalIndex}`;

                paths.push({
                  d: pathD,
                  color: stopColors[originalIndex],
                  markerId
                });
              });

              setArrowPaths(paths);
            };

            // Use useLayoutEffect to calculate after DOM is ready
            useLayoutEffect(() => {
              // Small delay to ensure all chips are rendered
              const timeoutId = setTimeout(() => {
                calculatePaths();
              }, 10);
              
              // Recalculate on window resize
              const handleResize = () => {
                calculatePaths();
              };
              window.addEventListener('resize', handleResize);
              
              return () => {
                clearTimeout(timeoutId);
                window.removeEventListener('resize', handleResize);
              };
            }, [optimizationResult]);

            return (
              <Box
                ref={arrowsContainerRef}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none',
                  zIndex: 1,
                  overflow: 'visible'
                }}
              >
                <svg
                  width="100%"
                  height="100%"
                  style={{ overflow: 'visible' }}
                >
                  <defs>
                    {arrowPaths.map((path) => (
                      <marker
                        key={path.markerId}
                        id={path.markerId}
                        markerWidth="10"
                        markerHeight="10"
                        refX="9"
                        refY="3"
                        orient="auto"
                      >
                        <polygon
                          points="0 0, 10 3, 0 6"
                          fill={path.color}
                        />
                      </marker>
                    ))}
                  </defs>
                  {arrowPaths.map((path, index) => (
                    <path
                      key={`path-${index}`}
                      d={path.d}
                      fill="none"
                      stroke={path.color}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      markerEnd={`url(#${path.markerId})`}
                    />
                  ))}
                </svg>
              </Box>
            );
          };

          return (
            <Box sx={{ display: 'flex', gap: 2, mb: 3, position: 'relative' }}>
              {/* Original Order */}
              <Box sx={{ flex: 1, position: 'relative' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t('routeBuilder.optimization.originalOrder')}
                </Typography>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', position: 'relative' }}>
                  {optimizationResult.originalOrder.map((stopName, originalIndex) => {
                    const optimizedIndex = originalToOptimized.get(originalIndex);
                    const moved = optimizedIndex !== undefined && optimizedIndex !== originalIndex;
                    return (
                      <Box 
                        key={`original-${originalIndex}`} 
                        id={`original-stop-${originalIndex}`}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          py: 1,
                          position: 'relative',
                          opacity: moved ? 1 : 0.7,
                          minHeight: 48
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1, pr: 1 }}>{stopName}</Typography>
                        <Chip
                          ref={(el) => {
                            if (!originalChipRefs.current) originalChipRefs.current = [];
                            originalChipRefs.current[originalIndex] = el;
                          }}
                          label={originalIndex + 1}
                          size="small"
                          sx={{ 
                            minWidth: 36,
                            height: 24,
                            backgroundColor: stopColors[originalIndex],
                            color: 'white',
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                      </Box>
                    );
                  })}
                </Paper>
              </Box>

              {/* Arrows Column - Visual connector between numbers */}
              <Box sx={{ 
                position: 'relative',
                minWidth: 120,
                py: 2,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                overflow: 'visible'
              }}>
                <ArrowConnector />
                
                {/* Placeholder boxes for alignment */}
                {optimizationResult.originalOrder.map((_, originalIndex) => (
                  <Box 
                    key={`arrow-placeholder-${originalIndex}`}
                    sx={{ 
                      height: 48,
                      width: '100%'
                    }}
                  />
                ))}
              </Box>

              {/* Optimized Order */}
              <Box sx={{ flex: 1, position: 'relative' }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: isAlreadyOptimal ? 'info.main' : 'success.main' }}>
                  {t('routeBuilder.optimization.optimizedOrder')}
                </Typography>
                <Paper variant="outlined" sx={{ 
                  p: 2, 
                  bgcolor: isAlreadyOptimal ? 'info.light' : 'success.light', 
                  borderColor: isAlreadyOptimal ? 'info.main' : 'success.main', 
                  borderWidth: 2,
                  position: 'relative'
                }}>
                  {optimizationResult.optimizedOrder.map((stopName, optimizedIndex) => {
                    const originalIndex = optimizedToOriginal.get(optimizedIndex);
                    const moved = originalIndex !== undefined && originalIndex !== optimizedIndex;
                    const stopColor = originalIndex !== undefined ? stopColors[originalIndex] : 
                      (isAlreadyOptimal ? 'var(--mui-palette-info-main)' : 'var(--mui-palette-success-main)');
                    return (
                      <Box 
                        key={`optimized-${optimizedIndex}`} 
                        id={`optimized-stop-${optimizedIndex}`}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          py: 1,
                          opacity: moved ? 1 : 0.7,
                          minHeight: 48
                        }}
                      >
                        <Chip
                          ref={(el) => {
                            if (!optimizedChipRefs.current) optimizedChipRefs.current = [];
                            optimizedChipRefs.current[optimizedIndex] = el;
                          }}
                          label={optimizedIndex + 1}
                          size="small"
                          sx={{ 
                            mr: 1, 
                            minWidth: 36,
                            height: 24,
                            backgroundColor: stopColor,
                            color: 'white',
                            fontWeight: 600,
                            '& .MuiChip-label': {
                              px: 1
                            }
                          }}
                        />
                        <Typography variant="body2" sx={{ flex: 1, fontWeight: moved ? 600 : 400 }}>
                          {stopName}
                        </Typography>
                      </Box>
                    );
                  })}
                </Paper>
              </Box>
            </Box>
          );
        })()}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={() => setOptimizationResult(null)}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 200 }}
        >
          {t('common.got_it')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Calculate loop duration, headway, and frequency for circulator routes
  const routeTrafficMultiplier = trafficMultiplier;
  const loopDuration = useMemo(() => {
    if (!isCirculator || stopConfigs.length < 2 || segments.length === 0) {
      return null;
    }
    const mockRoute: FixedRoute = {
      id: 'temp',
      name: 'temp',
      startTime: startTime,
      stops: stopConfigs,
      segments: segments,
      isCirculator: true,
      trafficMultiplier: routeTrafficMultiplier,
    };
    return calculateRouteLoopDuration(mockRoute, routeTrafficMultiplier);
  }, [isCirculator, stopConfigs, segments, routeTrafficMultiplier, startTime]);

  const headway = useMemo(() => {
    if (!loopDuration || !vehicleCount || vehicleCount <= 0) {
      return null;
    }
    return calculateHeadway(loopDuration, vehicleCount);
  }, [loopDuration, vehicleCount]);

  const frequency = useMemo(() => {
    if (!headway) {
      return null;
    }
    return calculateFrequency(headway);
  }, [headway]);

  if (showEditor) {
    const routeColor = selectedRoute?.color || generateRouteColor(selectedRoute?.id || 'new');
    const routeIsCirculator = isCirculator;

    return (
      <>
      <Card elevation={0} sx={{ boxShadow: 'none', border: 'none', backgroundColor: 'transparent' }}>
        <CardHeader
          sx={{ px: 0, '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' } }}
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="h6">
                {isCreating ? t('routeBuilder.editor.createTitle') : t('routeBuilder.editor.editTitle')}
              </Typography>
              {routeIsCirculator && (
                <Chip
                  label={t('routeBuilder.editor.circulator')}
                  size="small"
                  color="primary"
                  variant="outlined"
                  icon={<Icon size={20} name="repeat" />}
                  sx={{ height: 22 }}
                />
              )}
              {selectedRoute && selectedRoute.isManuallyEdited && (
                <Chip
                  label={t('routeBuilder.editor.manuallyEdited')}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 22 }}
                />
              )}
            </Box>
          }
          subheader={t('routeBuilder.editor.subheader')}
          action={
            isCreating ? (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!routeName.trim() || stopConfigs.length < 2 || (isCirculator && !endTime) || isSaving}
                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icon name="bus" />}
                size="small"
              >
                {isSaving ? t('routeBuilder.editor.creating') : t('routeBuilder.editor.createRoute')}
              </Button>
            ) : (
              !isCreating && selectedRoute && editingRouteId === selectedRoute.id && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Tooltip title={t('routeBuilder.editor.doneEditingTooltip')}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<Icon size={20} name="check" />}
                    onClick={() => exitEditMode()}
                    size="small"
                  >
                    {t('routeBuilder.editor.doneEditing')}
                  </Button>
                </Tooltip>
                {selectedRoute.isManuallyEdited && (
                  <Tooltip title={t('routeBuilder.editor.recalculateTooltip')}>
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        setShowRecalculateDialog(true);
                      }}
                      size="small"
                    >
                      {t('routeBuilder.editor.recalculateFromStops')}
                    </Button>
                  </Tooltip>
                )}
              </Box>
            )
            )
          }
        />
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, px: 0 }}>
          <Tabs
            value={editorTab}
            onChange={(_, newValue) => setEditorTab(newValue)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={t('routeBuilder.editor.tabs.stopSelection')} />
            <Tab label={t('routeBuilder.editor.tabs.routeDetails')} />
          </Tabs>

          {editorTab === 1 && (
          <Box>
            {/* Route Details Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Icon name="route" size={20} />
              <Typography variant="subtitle1" fontWeight="600">
                {t('routeBuilder.details.title')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pl: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <TextField
                  label={t('routeBuilder.details.routeName')}
                  value={routeName}
                  onChange={(e) => {
                    setRouteName(e.target.value);
                    routeNameManuallySet.current = true; // User manually entered name
                    if (validationErrors.routeName) {
                      setValidationErrors({ ...validationErrors, routeName: undefined });
                    }
                    // Save to history after a short delay (debounced)
                    setTimeout(() => saveToHistory(), 100);
                  }}
                  required
                  fullWidth
                  error={!!validationErrors.routeName}
                  helperText={validationErrors.routeName || (routeNameManuallySet.current ? t('routeBuilder.details.routeNameHelperManual') : t('routeBuilder.details.routeNameHelperAuto'))}
                  variant="outlined"
                  sx={{ flex: 1 }}
                />
                <FormControlLabel
                  sx={{ mt: 1, mr: 0, whiteSpace: 'nowrap' }}
                  control={
                    <Checkbox
                      checked={isBusRoute}
                      onChange={(e) => {
                        setIsBusRoute(e.target.checked);
                        setTimeout(() => saveToHistory(), 100);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2">
                      {t('routeBuilder.details.largeVehicleRouting')}
                    </Typography>
                  }
                />
                <FormControlLabel
                  sx={{ mt: 1, mr: 0, whiteSpace: 'nowrap' }}
                  control={
                    <Checkbox
                      checked={freehand}
                      onChange={(e) => {
                        setFreehand(e.target.checked);
                        logEvent({ category: 'fixed_routes', action: 'toggle_freehand', enabled: e.target.checked });
                        setTimeout(() => saveToHistory(), 100);
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title={t('routeBuilder.details.freehandHelper')}>
                      <Icon name="route" size={20} />
                      <Typography variant="body2">
                        {t('routeBuilder.details.freehand')}
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              {(() => {
                const liveSource = selectedRoute ? routes.find((r) => r.id === selectedRoute.id) : undefined;
                const hasReturn = Boolean(liveSource?.linkedRouteId && routes.some((r) => r.id === liveSource.linkedRouteId));
                if (!isEditing || isCirculator || stopConfigs.length < 2) return null;
                return (
                  <Button
                    variant="outlined"
                    startIcon={<Icon name="route" size={20} />}
                    onClick={handleCreateReturnRoute}
                    sx={{ alignSelf: 'flex-start' }}
                    title={t('routeBuilder.details.createReturnRouteHelper')}
                  >
                    {hasReturn
                      ? t('routeBuilder.details.viewReturnRoute')
                      : t('routeBuilder.details.createReturnRoute')}
                  </Button>
                );
              })()}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('routeBuilder.details.startTime')}
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    if (validationErrors.startTime) {
                      setValidationErrors({ ...validationErrors, startTime: undefined });
                    }
                    // Save to history after a short delay (debounced)
                    setTimeout(() => saveToHistory(), 100);
                  }}
                  placeholder="08:00"
                  required
                  helperText={validationErrors.startTime || t('routeBuilder.details.timeFormat')}
                  error={!!validationErrors.startTime}
                  InputProps={{
                    startAdornment: (
                      <Icon name="clock" size={20} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label={t('routeBuilder.details.endTime')}
                  value={endTime}
                  onChange={(e) => {
                    setEndTime(e.target.value);
                    if (validationErrors.endTime) {
                      setValidationErrors({ ...validationErrors, endTime: undefined });
                    }
                    // Save to history after a short delay (debounced)
                    setTimeout(() => saveToHistory(), 100);
                  }}
                  placeholder="22:00"
                  helperText={validationErrors.endTime || (isCirculator ? t('routeBuilder.details.requiredForCirculator') : t('routeBuilder.details.timeFormat'))}
                  error={!!validationErrors.endTime}
                  required={isCirculator}
                  InputProps={{
                    startAdornment: (
                      <Icon name="clock" size={20} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                    ),
                  }}
                  sx={{ flex: 1 }}
                />
              </Box>
            </Box>

              {/* Route Parameters */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'nowrap', mt: 1.5 }}>
                <TextField
                  label={t('routeBuilder.details.trafficMultiplier')}
                  size="small"
                  type="number"
                  inputProps={{
                    min: MIN_TRAFFIC_MULTIPLIER,
                    max: MAX_TRAFFIC_MULTIPLIER,
                    step: 0.1,
                  }}
                  value={trafficMultiplier}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= MIN_TRAFFIC_MULTIPLIER && value <= MAX_TRAFFIC_MULTIPLIER) {
                      setTrafficMultiplier(value);
                      if (validationErrors.trafficMultiplier) {
                        setValidationErrors({ ...validationErrors, trafficMultiplier: undefined });
                      }
                      setTimeout(() => saveToHistory(), 100);
                    }
                  }}
                  error={!!validationErrors.trafficMultiplier}
                  helperText={validationErrors.trafficMultiplier || t('routeBuilder.details.trafficHelper')}
                  InputProps={{
                    startAdornment: (
                      <Icon name="traffic-light" size={20} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                    ),
                  }}
                  sx={{ flex: '1 1 150px', minWidth: 140, maxWidth: 180 }}
                />
                <TextField
                  label={t('routeBuilder.details.waitTimeAll')}
                  size="small"
                  type="number"
                  inputProps={{
                    min: 0,
                    max: 60,
                    step: 0.5,
                  }}
                  value={(() => {
                    if (stopConfigs.length === 0) return DEFAULT_WAIT_TIME_MINUTES;
                    const firstWaitTime = stopConfigs[0]?.waitTimeMinutes || 0;
                    const allSame = stopConfigs.every(stop => stop.waitTimeMinutes === firstWaitTime);
                    return allSame ? firstWaitTime : '';
                  })()}
                  onChange={(e) => {
                    const value = parseFloat(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setStopConfigs(stopConfigs.map(stop => ({
                        ...stop,
                        waitTimeMinutes: value,
                      })));
                    }
                  }}
                  helperText={stopConfigs.length === 0 ? t('routeBuilder.details.waitTimeAddStops') : stopConfigs.every(stop => stop.waitTimeMinutes === stopConfigs[0]?.waitTimeMinutes) ? t('routeBuilder.details.waitTimeSame') : t('routeBuilder.details.waitTimeMixed')}
                  InputProps={{
                    startAdornment: (
                      <Icon name="clock" size={20} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                    ),
                  }}
                  sx={{ flex: '1 1 150px', minWidth: 140, maxWidth: 180 }}
                  disabled={stopConfigs.length === 0}
                  placeholder={stopConfigs.length === 0 ? '' : t('routeBuilder.details.mixedPlaceholder')}
                />
                <TextField
                  label={t('routeBuilder.details.numberOfVehicles')}
                  size="small"
                  type="number"
                  inputProps={{
                    min: 1,
                    step: 1,
                  }}
                  value={vehicleCount ?? (isCirculator ? '' : 1)}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    if (value === undefined || (!isNaN(value) && value >= 1 && Number.isInteger(value))) {
                      setVehicleCount(value);
                      if (validationErrors.vehicleCount) {
                        setValidationErrors({ ...validationErrors, vehicleCount: undefined });
                      }
                      setTimeout(() => saveToHistory(), 100);
                    } else {
                      setValidationErrors({
                        ...validationErrors,
                        vehicleCount: t('routeBuilder.validation.vehicleCountInteger'),
                      });
                    }
                  }}
                  error={!!validationErrors.vehicleCount}
                  helperText={validationErrors.vehicleCount || (isCirculator ? t('routeBuilder.details.vehiclesHelperCirculator') : t('routeBuilder.details.vehiclesHelperRegular'))}
                  InputProps={{
                    startAdornment: (
                      <Icon name="bus" size={20} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                    ),
                  }}
                  sx={{ flex: '1 1 150px', minWidth: 140, maxWidth: 180 }}
                />
                <TextField
                  label={t('routeBuilder.details.vehicleCapacity')}
                  size="small"
                  type="number"
                  inputProps={{
                    min: 1,
                    step: 1,
                  }}
                  value={vehicleCapacity || ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? undefined : parseInt(e.target.value, 10);
                    if (value === undefined || (!isNaN(value) && value >= 1 && Number.isInteger(value))) {
                      setVehicleCapacity(value);
                      setTimeout(() => saveToHistory(), 100);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <Icon name="user" size={20} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                    ),
                  }}
                  sx={{ flex: '1 1 150px', minWidth: 140, maxWidth: 180 }}
                />
              </Box>
              <Box sx={{ pt: 1 }}>
                {/* Display headway and frequency (only for circulator routes) */}
                {isCirculator && (
                  <>
                    {(headway !== null || frequency !== null) ? (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 0 }}>
                        {headway !== null && t('routeBuilder.details.headwayMinutes', { count: Math.round(headway), value: Math.round(headway) })}
                        {headway !== null && frequency !== null && ' • '}
                        {frequency !== null && t('routeBuilder.details.frequencyVehicles', { value: frequency.toFixed(1) })}
                      </Typography>
                    ) : vehicleCount !== undefined && vehicleCount >= 1 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 0 }}>
                        {t('routeBuilder.details.nAHelp')}
                      </Typography>
                    ) : null}
                  </>
                )}
              </Box>

              {/* GTFS service calendar + headsign/description (exported to GTFS) */}
              <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon name="clock" size={20} />
                  <Typography variant="subtitle2" fontWeight="600">
                    {t('routeBuilder.details.serviceDaysTitle', 'Service Days (GTFS)')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {([
                    ['monday', 'Mon'], ['tuesday', 'Tue'], ['wednesday', 'Wed'], ['thursday', 'Thu'],
                    ['friday', 'Fri'], ['saturday', 'Sat'], ['sunday', 'Sun'],
                  ] as Array<[keyof ServiceDaysMask, string]>).map(([key, label]) => {
                    const active = serviceDays ? serviceDays[key] : false;
                    return (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        clickable
                        color={active ? 'primary' : 'default'}
                        variant={active ? 'filled' : 'outlined'}
                        onClick={() => {
                          const base: ServiceDaysMask = serviceDays ?? {
                            monday: false, tuesday: false, wednesday: false, thursday: false,
                            friday: false, saturday: false, sunday: false,
                          };
                          setServiceDays({ ...base, [key]: !base[key] });
                        }}
                      />
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {serviceDays
                    ? t('routeBuilder.details.serviceDaysHelp', 'Days this route runs in the exported GTFS feed.')
                    : t('routeBuilder.details.serviceDaysDefault', 'Not set — GTFS export uses the feed default (Mon–Fri).')}
                </Typography>
                <TextField
                  label={t('routeBuilder.details.tripHeadsign', 'Trip Headsign (GTFS)')}
                  value={tripHeadsign}
                  onChange={(e) => setTripHeadsign(e.target.value)}
                  size="small"
                  fullWidth
                  helperText={t('routeBuilder.details.tripHeadsignHelp', 'Destination shown on vehicle signage. Defaults to the last stop name.')}
                  InputProps={{
                    startAdornment: (
                      <Icon name="bus" size={20} style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.54)' }} />
                    ),
                  }}
                />
                <TextField
                  label={t('routeBuilder.details.routeDesc', 'Route Description (GTFS)')}
                  value={routeDesc}
                  onChange={(e) => setRouteDesc(e.target.value)}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Box>
            </Box>
          )}

          {editorTab === 0 && (
          <>
          {/* Combined Stops Section */}
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle1" fontWeight="600">
                  {t('routeBuilder.stops.section')}
                </Typography>
                {stopConfigs.length > 0 && (
                  <Chip
                    label={t('routeBuilder.stops.stopsCount', { count: stopConfigs.length })}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
              {/* Distance Unit Toggle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('routeBuilder.stops.unitsLabel')}
                </Typography>
                <Chip
                  label={distanceUnit === 'metric' ? t('routeBuilder.stops.metric') : t('routeBuilder.stops.imperial')}
                  onClick={() => setDistanceUnit(distanceUnit === 'metric' ? 'imperial' : 'metric')}
                  size="small"
                  clickable
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: distanceUnit === 'metric' ? 'primary.main' : 'grey.300',
                    color: distanceUnit === 'metric' ? 'white' : 'text.primary',
                    '&:hover': {
                      backgroundColor: distanceUnit === 'metric' ? 'primary.dark' : 'grey.400',
                    },
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1.5 }}>
              {/* Band 1: route options */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1, py: 0.75, borderRadius: 1.5, backgroundColor: 'action.hover' }}>
                <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: '0.06em', color: 'text.secondary', textTransform: 'uppercase' }}>
                  {t('routeBuilder.stops.optionsLabel')}
                </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isCirculator}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      // Set default end time to 22:00 if circulator is enabled and endTime is empty
                      if (newValue && !endTime) {
                        setEndTime('22:00');
                      }
                      // Keep vehicle count at 1 (default) for both circulator and non-circulator routes
                      if (!vehicleCount) {
                        setVehicleCount(1);
                      }
                      // Save to history after a short delay (debounced)
                      setTimeout(() => saveToHistory(), 100);
                      const routeId = selectedRoute?.id ?? draftRouteIdRef.current ?? draftRouteId;
                      const routeFromContext = routeId ? routes.find((r) => r.id === routeId) : undefined;
                      const routeSource = routeFromContext ?? draftRouteSnapshotRef.current ?? selectedRoute;
                      let nextSegments = segments;
                      let restoredCirculatorGeometry: FixedRoute['geometry'] | undefined;

                      if (newValue) {
                        if (stopConfigs.length >= 2) {
                          const lastStopId = stopConfigs[stopConfigs.length - 1].stopId;
                          const firstStopId = stopConfigs[0].stopId;
                          const cache = circulatorReturnLegCacheRef.current;
                          if (cache && cache.stopSequenceKey === getStopSequenceKey(stopConfigs)) {
                            const filtered = segments.filter(
                              (seg) => !(seg.fromStopId === lastStopId && seg.toStopId === firstStopId)
                            );
                            nextSegments = [...filtered, cache.segment];
                            restoredCirculatorGeometry = cache.geometry;
                          } else {
                            const merged = await calculateReturnSegment({
                              circulatorActive: true,
                              applyState: false,
                            });
                            if (merged) nextSegments = merged;
                          }
                        }
                      } else if (stopConfigs.length >= 2) {
                        // If disabling circulator, remove return segment
                        const lastStopId = stopConfigs[stopConfigs.length - 1].stopId;
                        const firstStopId = stopConfigs[0].stopId;
                        const returnSegmentKey = `${lastStopId}-${firstStopId}`;
                        const returnSegment = segments.find(
                          (seg) => seg.fromStopId === lastStopId && seg.toStopId === firstStopId
                        );
                        if (returnSegment) {
                          circulatorReturnLegCacheRef.current = {
                            stopSequenceKey: getStopSequenceKey(stopConfigs),
                            segment: { ...returnSegment },
                            geometry: routeSource?.isCirculator ? routeSource.geometry : undefined,
                          };
                        }
                        // Remove from manually edited set if it was there
                        const newManuallyEdited = new Set(manuallyEditedSegments);
                        newManuallyEdited.delete(returnSegmentKey);
                        setManuallyEditedSegments(newManuallyEdited);
                        nextSegments = segments.filter(
                          (seg) => !(seg.fromStopId === lastStopId && seg.toStopId === firstStopId)
                        );
                      }

                      setIsCirculator(newValue);
                      setSegments(nextSegments);

                      if (routeSource) {
                        const nextGeometry = newValue
                          ? (restoredCirculatorGeometry ?? routeSource.geometry)
                          : undefined;
                        const updatedRoute: FixedRoute = {
                          ...routeSource,
                          startTime,
                          endTime: (newValue && !endTime ? '22:00' : endTime) || undefined,
                          stops: stopConfigs,
                          segments: nextSegments,
                          trafficMultiplier:
                            trafficMultiplier !== DEFAULT_TRAFFIC_MULTIPLIER ? trafficMultiplier : undefined,
                          isCirculator: newValue,
                          reverseReturnJourney,
                          isBusRoute,
                          vehicleCount: vehicleCount && vehicleCount >= 1 ? vehicleCount : (newValue ? undefined : 1),
                          vehicleCapacity: vehicleCapacity && vehicleCapacity > 0 ? vehicleCapacity : 50,
                          waypoints: undefined,
                          waypointStopIndices: undefined,
                          // Turning circulator off must drop cached loop geometry so return leg disappears.
                          geometry: nextGeometry,
                        };
                        updateRoute(updatedRoute);
                        setSelectedRoute(updatedRoute);
                        draftRouteSnapshotRef.current = updatedRoute;
                      } else if (stopConfigs.length >= 2) {
                        upsertDraftRoute(
                          stopConfigs,
                          captureWaypoints,
                          captureWaypointStopIndices,
                          nextSegments,
                          {
                            isCirculatorOverride: newValue,
                            endTimeOverride: newValue && !endTime ? '22:00' : endTime,
                          }
                        );
                      }

                      // Turning circulator ON: tell the map to show a dotted, throbbing line
                      // back to the origin (last stop -> first stop) while the return leg routes.
                      // The map clears it automatically once the real return geometry closes the loop.
                      if (newValue && routeId && stopConfigs.length >= 2) {
                        window.dispatchEvent(new CustomEvent('fixedRouteReturnLegLoading', {
                          detail: { routeId },
                        }));
                      }
                    }}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon name="repeat" size={20} />
                    <Typography variant="body2">
                      {t('routeBuilder.stops.circulatorRoute')}
                    </Typography>
                  </Box>
                }
                sx={{ m: 0 }}
              />
              {!isCirculator && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={reverseReturnJourney}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        setReverseReturnJourney(newValue);
                        setTimeout(() => saveToHistory(), 100);

                        const routeId = selectedRoute?.id ?? draftRouteIdRef.current ?? draftRouteId;
                        const routeFromContext = routeId ? routes.find((r) => r.id === routeId) : undefined;
                        const routeSource = routeFromContext ?? draftRouteSnapshotRef.current ?? selectedRoute;

                        if (routeSource) {
                          const updatedRoute: FixedRoute = {
                            ...routeSource,
                            reverseReturnJourney: newValue,
                          };
                          updateRoute(updatedRoute);
                          setSelectedRoute(updatedRoute);
                          draftRouteSnapshotRef.current = updatedRoute;
                        } else if (stopConfigs.length >= 2) {
                          upsertDraftRoute(
                            stopConfigs,
                            captureWaypoints,
                            captureWaypointStopIndices,
                            segments,
                            {
                              reverseReturnJourneyOverride: newValue,
                            }
                          );
                        }
                      }}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <SwapVertIcon fontSize="small" />
                      <Typography variant="body2">
                        {t('routeBuilder.stops.reverseReturnJourney')}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0 }}
                />
              )}
              <Tooltip
                title={(
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t('routeBuilder.stops.captureInstructionsTitle')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      {t('routeBuilder.stops.captureClickHint')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {t('routeBuilder.stops.captureInsertHint')}
                    </Typography>
                  </Box>
                )}
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: { zIndex: (theme) => theme.zIndex.drawer + 5 },
                  },
                }}
              >
                <IconButton
                  size="small"
                  aria-label={t('routeBuilder.stops.captureInstructionsAria')}
                  sx={{ color: 'text.secondary' }}
                >
                  <Icon name="circle-info" size={20} />
                </IconButton>
              </Tooltip>
              </Box>

              {/* Row 1: add a new location — search + pick on map */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Autocomplete
                freeSolo
                size="small"
                sx={{ minWidth: 200, flex: '1 1 220px' }}
                options={addressOptions}
                loading={addressLoading}
                filterOptions={(opts) => opts}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.displayName)}
                isOptionEqualToValue={(option, value) =>
                  typeof option !== 'string' && typeof value !== 'string' && option.lat === value.lat && option.lng === value.lng
                }
                inputValue={addressInput}
                onInputChange={(_, value) => setAddressInput(value)}
                onChange={(_, value) => {
                  if (value && typeof value !== 'string') handleAddStopFromSearch(value);
                }}
                slotProps={{ popper: { sx: { zIndex: (theme) => theme.zIndex.drawer + 6 } } }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('routeBuilder.stops.addByAddressPlaceholder')}
                    inputProps={{ ...params.inputProps, 'aria-label': t('routeBuilder.stops.addByAddress') }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />,
                      endAdornment: (
                        <>
                          {addressLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <Tooltip
                title={(
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {isRouteCaptureMode ? t('routeBuilder.stops.disableCapture') : t('routeBuilder.stops.enableCapture')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      {t('routeBuilder.stops.captureClickHint')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {t('routeBuilder.stops.captureInsertHint')}
                    </Typography>
                  </Box>
                )}
                placement="top"
                arrow
                slotProps={{
                  popper: {
                    sx: { zIndex: (theme) => theme.zIndex.drawer + 5 },
                  },
                }}
              >
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Icon size={20} d={ICON_FALLBACKS.AddLocationAlt} />}
                    onClick={toggleRouteCapture}
                    disabled={isCaptureBusy}
                    aria-pressed={isRouteCaptureMode}
                    color={isRouteCaptureMode ? 'primary' : 'inherit'}
                    sx={{ color: isRouteCaptureMode ? undefined : 'text.secondary', whiteSpace: 'nowrap', height: 40 }}
                  >
                    {t('routeBuilder.stops.pickOnMap')}
                  </Button>
                </span>
              </Tooltip>
              </Box>

              {/* Row 2: add from existing stops — select / from layer / add all */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180, flex: '0 1 240px' }}>
                <InputLabel>{t('routeBuilder.stops.selectStop')}</InputLabel>
                <Select
                  value={selectedStopId}
                  onChange={(e) => {
                    const stopId = e.target.value as string;
                    handleAddStop(stopId);
                  }}
                  label={t('routeBuilder.stops.selectStop')}
                  MenuProps={{
                    sx: { zIndex: (theme) => theme.zIndex.drawer + 5 },
                    PaperProps: {
                      sx: { zIndex: (theme) => theme.zIndex.drawer + 6 },
                    },
                  }}
                >
                  {availableStops.map((stop) => (
                    <MenuItem key={stop.id} value={stop.id}>
                      {stop.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180, flex: '0 1 220px' }} disabled={layerStopOptions.length === 0}>
                <InputLabel id="route-builder-add-from-layer-label">
                  {t('routeBuilder.stops.addFromLayer')}
                </InputLabel>
                <Select
                  labelId="route-builder-add-from-layer-label"
                  value={addFromLayerSelection}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      return;
                    }
                    const layerId = Number(raw);
                    if (!Number.isFinite(layerId)) {
                      return;
                    }
                    handleAddStopsFromLayer(layerId);
                    setAddFromLayerSelection('');
                  }}
                  label={t('routeBuilder.stops.addFromLayer')}
                  MenuProps={{
                    sx: { zIndex: (theme) => theme.zIndex.drawer + 5 },
                    PaperProps: {
                      sx: { zIndex: (theme) => theme.zIndex.drawer + 6 },
                    },
                  }}
                >
                  {layerStopOptions.map((option) => (
                    <MenuItem key={option.layerId} value={String(option.layerId)}>
                      {t('routeBuilder.stops.addFromLayerOption', {
                        layerName: option.layerName,
                        count: option.count,
                      })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Icon size={20} name="plus" />}
                onClick={handleAddAllStops}
                disabled={availableStops.length === 0 || availableStops.every(stop => stopConfigs.find(s => s.stopId === stop.id))}
                sx={{ minWidth: 140, height: 40 }}
                title={
                  availableStops.length === 0
                    ? t('routeBuilder.stops.addAllNone')
                    : t('routeBuilder.stops.addAllRemaining', { count: availableStops.filter(stop => !stopConfigs.find(s => s.stopId === stop.id)).length })
                }
              >
                {t('routeBuilder.stops.addAllStops')}
              </Button>
              </Box>
            </Box>
            <Box>
              {stopConfigs.length >= 2 && (
                <Box sx={{ mb: 2 }}>
                  {/* Compact single-row action toolbar. Full labels wrap to 2-3 lines in the narrow
                      drawer (making the buttons tall), so these are equal-width icon buttons with
                      tooltips — one row, easy to scan, and they leave room for the route below. */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Tooltip title={isCalculating ? t('routeBuilder.stops.calculating') : t('routeBuilder.stops.calculateTravelTimes')}>
                      <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          aria-label={t('routeBuilder.stops.calculateTravelTimes')}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCalculateAllTravelTimes();
                          }}
                          disabled={isCalculating || isOptimizing || stopConfigs.length < 2}
                          sx={{ minWidth: 0, height: 40 }}
                        >
                          {isCalculating ? <CircularProgress size={18} /> : <Icon d={ICON_FALLBACKS.Calculate} size={20} />}
                        </Button>
                      </Box>
                    </Tooltip>
                    <Tooltip title={isOptimizing ? t('routeBuilder.stops.optimizing') : t('routeBuilder.stops.optimizeOrder')}>
                      <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          color="secondary"
                          aria-label={t('routeBuilder.stops.optimizeOrder')}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleOptimizeRouteOrder();
                          }}
                          disabled={isCalculating || isOptimizing || stopConfigs.length < 2}
                          sx={{ minWidth: 0, height: 40 }}
                        >
                          {isOptimizing ? <CircularProgress size={18} color="secondary" /> : <Icon name="sliders" size={20} />}
                        </Button>
                      </Box>
                    </Tooltip>
                    <Tooltip title={t('routeBuilder.stops.reverseDirectionTooltip')}>
                      <Box component="span" sx={{ flex: 1, display: 'flex' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          aria-label={t('routeBuilder.stops.reverseDirection')}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            void handleReverseDirection();
                          }}
                          disabled={isCalculating || isOptimizing || stopConfigs.length < 2}
                          sx={{ minWidth: 0, height: 40 }}
                        >
                          <Icon d={ICON_FALLBACKS.SwapHoriz} size={20} />
                        </Button>
                      </Box>
                    </Tooltip>
                  </Box>
                  {(calculationProgress || isOptimizing) && (
                    <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                      {calculationProgress || t('routeBuilder.stops.optimizingMessage')}
                    </Typography>
                  )}
                  {calculationError && (
                    <Alert 
                      severity={failedSegments.length === stopConfigs.length - 1 ? "error" : "warning"} 
                      sx={{ mb: 1 }} 
                      onClose={() => {
                        setCalculationError(null);
                        setFailedSegments([]);
                      }}
                    >
                      {calculationError}
                    </Alert>
                  )}
                </Box>
              )}
              <StopSequenceEditor
                stops={stopConfigs}
                allStops={stops.map((s) => ({ id: s.id, name: s.name }))}
                onReorder={handleReorder}
                onRemove={handleRemoveStop}
                onUpdateWaitTime={handleUpdateWaitTime}
                onUpdateTravelTime={handleUpdateTravelTime}
                segments={segments}
                trafficMultiplier={trafficMultiplier}
                waitInputsEnabled={waitInputsEnabled}
              />
              {stopConfigs.length < 2 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {t('routeBuilder.stops.minStopsAlert')}
                </Alert>
              )}
            </Box>
          </Box>
          </>
          )}

          <Divider />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{ minWidth: 100 }}
            >
              {t('routeBuilder.actions.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!routeName.trim() || stopConfigs.length < 2 || (isCirculator && !endTime) || isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Icon name="bus" />}
              sx={{ minWidth: 150 }}
            >
              {isSaving ? (isCreating ? t('routeBuilder.editor.creating') : t('routeBuilder.editor.saving')) : (isCreating ? t('routeBuilder.editor.createRoute') : t('routeBuilder.editor.saveChanges'))}
            </Button>
          </Box>
        </CardContent>

        {optimizationModal}
      </Card>
      
      {/* Snackbar for save notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
    );
  }

  return (
    <>
      <Card>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon size={20} name="route" />
            <Typography variant="h6">{t('routeBuilder.list.title')}</Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<Icon size={20} name="plus" />}
            onClick={handleCreateNew}
          >
            {t('routeBuilder.list.createRoute')}
          </Button>
        }
        subheader={t('routeBuilder.list.subheader')}
      />
      <CardContent>
        {stops.length < 2 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('routeBuilder.list.needTwoStops')}
          </Alert>
        )}

        {routes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              {t('routeBuilder.list.noRoutesCreated')}
            </Typography>
          </Box>
        ) : (
          <Box ref={routeListRef} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* Search and Sort Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                fullWidth
                placeholder={t('routeBuilder.list.searchPlaceholder')}
                value={routeSearchQuery}
                onChange={(e) => setRouteSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <Icon size={20} name="magnifying-glass" style={{ color: '#52565e', marginRight: 8 }} />,
                }}
                size="small"
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>{t('routeBuilder.list.sortBy')}</InputLabel>
                <Select
                  value={routeSortBy}
                  onChange={(e) => setRouteSortBy(e.target.value as typeof routeSortBy)}
                  label={t('routeBuilder.list.sortBy')}
                >
                  <MenuItem value="name-asc">{t('routeBuilder.list.sortOptions.nameAsc')}</MenuItem>
                  <MenuItem value="name-desc">{t('routeBuilder.list.sortOptions.nameDesc')}</MenuItem>
                  <MenuItem value="start-time-asc">{t('routeBuilder.list.sortOptions.startTimeAsc')}</MenuItem>
                  <MenuItem value="start-time-desc">{t('routeBuilder.list.sortOptions.startTimeDesc')}</MenuItem>
                  <MenuItem value="stops-desc">{t('routeBuilder.list.sortOptions.stopsDesc')}</MenuItem>
                  <MenuItem value="stops-asc">{t('routeBuilder.list.sortOptions.stopsAsc')}</MenuItem>
                  <MenuItem value="distance-desc">{t('routeBuilder.list.sortOptions.distanceDesc')}</MenuItem>
                  <MenuItem value="distance-asc">{t('routeBuilder.list.sortOptions.distanceAsc')}</MenuItem>
                  <MenuItem value="type">{t('routeBuilder.list.sortOptions.type')}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {/* Filtered Routes */}
            {(() => {
              const filteredRoutes = routes.filter((route) => {
                if (!routeSearchQuery.trim()) return true;
                
                const query = routeSearchQuery.toLowerCase();
                const routeName = route.name.toLowerCase();
                const startTime = route.startTime.toLowerCase();
                const endTime = (route.endTime || '').toLowerCase();
                const stopsCount = route.stops.length.toString();
                const isCirculatorText = route.isCirculator ? 'circulator' : '';
                const gtfsRouteId = (route.gtfsRouteId || '').toLowerCase();
                
                // Search stop names in this route
                const stopNamesMatch = route.stops.some((stopConfig) => {
                  const stop = stops.find((s) => s.id === stopConfig.stopId);
                  return stop && stop.name.toLowerCase().includes(query);
                });
                
                return (
                  routeName.includes(query) ||
                  startTime.includes(query) ||
                  endTime.includes(query) ||
                  stopsCount.includes(query) ||
                  isCirculatorText.includes(query) ||
                  gtfsRouteId.includes(query) ||
                  stopNamesMatch
                );
              });

              if (filteredRoutes.length === 0) {
                return (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('routeBuilder.list.noMatch', { query: routeSearchQuery })}
                    </Typography>
                  </Box>
                );
              }

              // Helper function to calculate route distance
              const calculateRouteDistance = (route: FixedRoute): number => {
                const isCirculator = route.isCirculator || false;
                const routeTrafficMultiplier = route.trafficMultiplier ?? globalTrafficMultiplier;
                let totalDistance = 0;
                
                route.segments.forEach((segment) => {
                  const isReturnSegment = segment.fromStopId === route.stops[route.stops.length - 1]?.stopId &&
                                           segment.toStopId === route.stops[0]?.stopId;
                  
                  // Only count return segment if it's a circulator route
                  if (!isReturnSegment || isCirculator) {
                    totalDistance += segment.distanceMeters || 0;
                  }
                });
                
                return totalDistance;
              };

              // Sort filtered routes
              const sortedRoutes = [...filteredRoutes].sort((a, b) => {
                switch (routeSortBy) {
                  case 'name-asc':
                    return a.name.localeCompare(b.name);
                  case 'name-desc':
                    return b.name.localeCompare(a.name);
                  case 'start-time-asc': {
                    const aTime = timeToMinutes(a.startTime);
                    const bTime = timeToMinutes(b.startTime);
                    return aTime - bTime;
                  }
                  case 'start-time-desc': {
                    const aTime = timeToMinutes(a.startTime);
                    const bTime = timeToMinutes(b.startTime);
                    return bTime - aTime;
                  }
                  case 'stops-desc':
                    return b.stops.length - a.stops.length;
                  case 'stops-asc':
                    return a.stops.length - b.stops.length;
                  case 'distance-desc': {
                    const aDistance = calculateRouteDistance(a);
                    const bDistance = calculateRouteDistance(b);
                    return bDistance - aDistance;
                  }
                  case 'distance-asc': {
                    const aDistance = calculateRouteDistance(a);
                    const bDistance = calculateRouteDistance(b);
                    return aDistance - bDistance;
                  }
                  case 'type': {
                    // Circulators first, then regular routes
                    const aIsCirc = a.isCirculator ? 0 : 1;
                    const bIsCirc = b.isCirculator ? 0 : 1;
                    if (aIsCirc !== bIsCirc) return aIsCirc - bIsCirc;
                    return a.name.localeCompare(b.name);
                  }
                  default:
                    return 0;
                }
              });

              return sortedRoutes.map((route) => {
              const routeColor = route.color || generateRouteColor(route.id);
              const isCirculator = route.isCirculator || false;
              
              // Get route-specific traffic multiplier or fall back to global
              const routeTrafficMultiplier = route.trafficMultiplier ?? globalTrafficMultiplier;
              
              // Calculate total travel time and distance (with traffic multiplier applied)
              let totalTravelTime = 0;
              let totalTravelDistance = 0;
              
              // Sum all segments (excluding return segment for now)
              route.segments.forEach((segment) => {
                // Check if this is a return segment (from last stop to first stop)
                const isReturnSegment = segment.fromStopId === route.stops[route.stops.length - 1]?.stopId &&
                                         segment.toStopId === route.stops[0]?.stopId;
                
                // Only count return segment if it's a circulator route
                if (!isReturnSegment || isCirculator) {
                  // Apply traffic multiplier to travel time for display
                  const adjustedTravelTime = segment.travelTimeMinutes 
                    ? Math.round(segment.travelTimeMinutes * routeTrafficMultiplier)
                    : 0;
                  totalTravelTime += adjustedTravelTime;
                  totalTravelDistance += segment.distanceMeters || 0;
                }
              });
              
              // Calculate total wait time (sum of all stop wait times)
              const totalWaitTime = route.stops.reduce((sum, stop) => sum + stop.waitTimeMinutes, 0);
              
              // Calculate total route time (travel time + wait time)
              const totalRouteTime = totalTravelTime + totalWaitTime;
              
              // For circulator routes, calculate complete loop duration (includes return segment)
              const loopDuration = isCirculator 
                ? calculateRouteLoopDuration(route, routeTrafficMultiplier)
                : null;
              
              // Calculate headway and frequency for circulator routes with vehicle count
              const routeHeadway = isCirculator && route.vehicleCount && route.vehicleCount > 0 && loopDuration
                ? calculateHeadway(loopDuration, route.vehicleCount)
                : null;
              const routeFrequency = routeHeadway
                ? calculateFrequency(routeHeadway)
                : null;
              
              // Calculate operating duration (end time - start time)
              let operatingDuration: number | null = null;
              if (route.endTime) {
                const startMinutes = timeToMinutes(route.startTime);
                const endMinutes = timeToMinutes(route.endTime);
                operatingDuration = endMinutes >= startMinutes 
                  ? endMinutes - startMinutes 
                  : (endMinutes + 24 * 60) - startMinutes; // Handle wrap-around
              }

              // Calculate daily and hourly capacity
              const DEFAULT_CAPACITY = 50;
              const capacity = route.vehicleCapacity ?? DEFAULT_CAPACITY;
              const vehicleCountForCapacity = route.vehicleCount ?? 1; // Default to 1 if not set
              let dailyCapacity = 0;
              let hourlyCapacity = 0;

              if (isCirculator && vehicleCountForCapacity >= 1 && loopDuration && loopDuration > 0) {
                // Circulator route capacity calculation
                if (operatingDuration && operatingDuration > 0) {
                  // Daily capacity: trips per day × capacity
                  const tripsPerDay = (operatingDuration / loopDuration) * vehicleCountForCapacity;
                  dailyCapacity = Math.round(tripsPerDay * capacity);
                }
                
                // Hourly capacity: trips per hour × capacity
                if (routeHeadway && routeHeadway > 0) {
                  const tripsPerHour = 60 / routeHeadway;
                  hourlyCapacity = Math.round(tripsPerHour * capacity);
                }
              } else if (route.tripSchedules && route.tripSchedules.length > 0) {
                // Scheduled route capacity calculation
                const tripsPerDay = route.tripSchedules.length;
                dailyCapacity = tripsPerDay * capacity * vehicleCountForCapacity;
                
                // Calculate hourly capacity: find peak hour
                const tripsByHour = new Map<number, number>();
                route.tripSchedules.forEach((trip) => {
                  const hour = Math.floor(trip.startTimeMinutes / 60) % 24;
                  tripsByHour.set(hour, (tripsByHour.get(hour) || 0) + 1);
                });
                
                const maxTripsInHour = Math.max(...Array.from(tripsByHour.values()), 0);
                hourlyCapacity = maxTripsInHour * capacity * vehicleCountForCapacity;
              } else {
                // Single trip route (non-circulator, no schedules)
                // Assume 1 trip per day, capacity = vehicle capacity × number of vehicles
                dailyCapacity = capacity * vehicleCountForCapacity;
                hourlyCapacity = capacity * vehicleCountForCapacity;
              }
              
              // Helper function to convert time string to minutes
              function timeToMinutes(timeStr: string): number {
                const parts = timeStr.split(':');
                const hours = parseInt(parts[0] || '0', 10);
                const minutes = parseInt(parts[1] || '0', 10);
                return hours * 60 + minutes;
              }

              // Operating cost (daily + annual) using network-default rates and any per-route override.
              const routeCost = computeRouteCost(route, costSettings);

              return (
                <Card
                  key={route.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    borderLeft: `${editingRouteId === route.id ? 6 : 4}px solid ${routeColor}`,
                    // Make the route currently under edit visually unmistakable in the list (BUG #15b):
                    // tinted background + active ring, consistent with MUI selected styling.
                    ...(editingRouteId === route.id
                      ? {
                          backgroundColor: 'action.selected',
                          boxShadow: (theme) => `0 0 0 2px ${theme.palette.info.main}`,
                        }
                      : {}),
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateX(2px)',
                    },
                  }}
                  onClick={() => handleSelectRoute(route)}
                  onContextMenu={(e) => handleContextMenu(e, route)}
                >
                  <CardContent sx={{ pb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            backgroundColor: routeColor,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                          }}
                        >
                          <Icon name="bus" size={20} />
                        </Box>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Box>
                              <Typography variant="subtitle1" fontWeight="600">
                                {route.name}
                              </Typography>
                              {route.shortName && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                                  {route.shortName}
                                </Typography>
                              )}
                            </Box>
                            {isCirculator && (
                              <Chip
                                label={t('routeBuilder.list.circulator')}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 20 }}
                              />
                            )}
                            {route.isManuallyEdited && (
                              <Chip
                                label={t('routeBuilder.list.manuallyEdited')}
                                size="small"
                                color="warning"
                                variant="outlined"
                                sx={{ height: 20 }}
                              />
                            )}
                            {route.networkOrigin && (
                              <Chip
                                icon={<SensorsIcon />}
                                label={t('routeBuilder.list.networkCopy')}
                                size="small"
                                color="secondary"
                                variant="outlined"
                                sx={{ height: 20 }}
                              />
                            )}
                            {editingRouteId === route.id && (
                              <Chip
                                label={t('routeBuilder.list.editing')}
                                size="small"
                                color="info"
                                variant="filled"
                                sx={{ height: 20 }}
                              />
                            )}
                          </Box>
                        </Box>
                      </Box>
                      <Box>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openRouteOverlapModal(route.id);
                          }}
                          aria-label={`Analyze overlap for ${route.name}`}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'warning.light',
                              color: 'warning.contrastText',
                            },
                          }}
                          title="Analyze overlap"
                        >
                          <TrendingUpIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectRoute(route);
                          }}
                          aria-label={t('routeBuilder.list.editAria', { name: route.name })}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'action.selected',
                            },
                          }}
                        >
                          <Icon name="pen-to-square" size={20} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportRouteExcel(route);
                          }}
                          aria-label={t('routeBuilder.list.exportExcelAria', { name: route.name })}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'success.light',
                              color: 'success.contrastText',
                            },
                          }}
                          title={t('routeBuilder.list.exportExcelTitle')}
                        >
                          <Icon name="file-arrow-down" size={20} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportRouteKML(route);
                          }}
                          aria-label={t('routeBuilder.list.exportKmlAria', { name: route.name })}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'info.light',
                              color: 'info.contrastText',
                            },
                          }}
                          title={t('routeBuilder.list.exportKmlTitle')}
                        >
                          <Icon name="map" size={20} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(route.id);
                          }}
                          aria-label={t('routeBuilder.list.deleteAria', { name: route.name })}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'error.light',
                              color: 'error.contrastText',
                            },
                          }}
                        >
                          <Icon name="trash" size={20} />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Route Summary — one wrapping row so the operating time, stop count,
                        timing and capacity numbers share a line and only break to a new line
                        when they don't fit (rather than capacity always starting its own row).
                        columnGap spaces items horizontally; the smaller rowGap keeps wrapped
                        lines tight. */}
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: compactRouteMetrics ? 1.25 : 2, rowGap: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Icon name="clock" size={20} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            <strong>{route.startTime}</strong> - <strong>{route.endTime || t('common.notAvailable')}</strong>
                            {!compactRouteMetrics && operatingDuration !== null && (
                              <>{t('routeBuilder.list.operatingDuration', { duration: formatTotalTime(operatingDuration) })}</>
                            )}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Icon name="location-dot" size={20} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {compactRouteMetrics
                              ? route.stops.length
                              : t('routeBuilder.list.stopsLabel', { count: route.stops.length })}
                          </Typography>
                        </Box>
                        {isCirculator && loopDuration !== null && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="repeat" size={20} />
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 600 }}>
                              {compactRouteMetrics
                                ? formatTotalTime(loopDuration)
                                : t('routeBuilder.list.loopLabel', { value: formatTotalTime(loopDuration) })}
                            </Typography>
                          </Box>
                        )}
                        {totalRouteTime > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="clock" size={20} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {compactRouteMetrics
                                ? formatTotalTime(totalRouteTime)
                                : t('routeBuilder.list.totalLabel', { value: formatTotalTime(totalRouteTime) })}
                            </Typography>
                          </Box>
                        )}
                        {totalTravelTime > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="route" size={20} />
                            <Typography variant="body2" color="text.secondary">
                              {compactRouteMetrics
                                ? formatTotalTime(totalTravelTime)
                                : t('routeBuilder.list.travelLabel', { value: formatTotalTime(totalTravelTime) })}
                            </Typography>
                          </Box>
                        )}
                        {totalWaitTime > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="hourglass-half" size={20} />
                            <Typography variant="body2" color="text.secondary">
                              {compactRouteMetrics
                                ? formatTotalTime(totalWaitTime)
                                : t('routeBuilder.list.waitLabel', { value: formatTotalTime(totalWaitTime) })}
                            </Typography>
                          </Box>
                        )}
                        {(route.vehicleCount && route.vehicleCount >= 1) && (
                          <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Icon name="bus" size={20} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {compactRouteMetrics
                                  ? route.vehicleCount
                                  : t('routeBuilder.list.vehicles', { count: route.vehicleCount })}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 0.5 }}>
                                <Icon name="user" size={20} />
                                <Typography variant="body2" color="text.secondary">
                                  {compactRouteMetrics
                                    ? (route.vehicleCapacity ?? 50)
                                    : t('routeBuilder.list.capacityLabel', { value: route.vehicleCapacity ?? 50 })}
                                </Typography>
                              </Box>
                            </Box>
                            {isCirculator && routeHeadway !== null && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Icon d={ICON_FALLBACKS.Speed} size={20} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  {compactRouteMetrics
                                    ? Math.round(routeHeadway)
                                    : t('routeBuilder.list.headwayLabel', { value: Math.round(routeHeadway) })}
                                </Typography>
                              </Box>
                            )}
                            {isCirculator && routeFrequency !== null && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Icon d={ICON_FALLBACKS.TrendingUp} size={20} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  {compactRouteMetrics
                                    ? routeFrequency.toFixed(1)
                                    : t('routeBuilder.list.frequencyLabel', { value: routeFrequency.toFixed(1) })}
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                        {(dailyCapacity > 0 || hourlyCapacity > 0) && (
                          <>
                            {dailyCapacity > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Icon name="calendar-day" size={20} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  {compactRouteMetrics
                                    ? dailyCapacity.toLocaleString()
                                    : t('routeBuilder.list.dailyCapacityLabel', { value: dailyCapacity.toLocaleString() })}
                                </Typography>
                              </Box>
                            )}
                            {hourlyCapacity > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Icon name="users" size={20} />
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  {compactRouteMetrics
                                    ? hourlyCapacity.toLocaleString()
                                    : t('routeBuilder.list.hourlyCapacityLabel', { value: hourlyCapacity.toLocaleString() })}
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                        {totalTravelDistance > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="map" size={20} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {formatDistance(totalTravelDistance, distanceUnit)}
                            </Typography>
                          </Box>
                        )}
                        {routeCost.dailyCost > 0 && (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            title={routeCost.hasOverride ? t('routeBuilder.list.costOverrideTitle') : t('routeBuilder.list.costTitle')}
                          >
                            <Icon d={routeCost.hasOverride ? ICON_FALLBACKS.AutoFixHigh : ICON_FALLBACKS.AttachMoney} size={20} />
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, color: routeCost.hasOverride ? 'secondary.main' : 'success.main' }}
                            >
                              {compactRouteMetrics
                                ? formatCurrency(routeCost.dailyCost, costSettings.currency)
                                : `${t('routeBuilder.list.costPerDay', { value: formatCurrency(routeCost.dailyCost, costSettings.currency) })} · ${t('routeBuilder.list.costPerYear', { value: formatCurrencyCompact(routeCost.annualCost, costSettings.currency) })}`}
                            </Typography>
                          </Box>
                        )}
                        {routeTrafficMultiplier !== 1.0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="traffic-light" size={20} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {compactRouteMetrics
                                ? `${routeTrafficMultiplier.toFixed(2)}x`
                                : t('routeBuilder.list.trafficLabel', { value: routeTrafficMultiplier.toFixed(2) })}
                            </Typography>
                          </Box>
                        )}
                    </Box>
                    {/* Stops are collapsed behind an accordion so a route only takes up a
                        compact "first stop → N stops → last stop" summary by default. This keeps
                        multi-route networks readable in the drawer; expanding reveals the full
                        per-stop list with travel times. stopPropagation keeps toggling the
                        accordion from also selecting/editing the route (the Card onClick). */}
                    {route.stops.length > 0 && (() => {
                      const firstStopName =
                        stops.find((s) => s.id === route.stops[0].stopId)?.name ||
                        t('routeBuilder.list.stopFallback', { number: 1 });
                      const lastIdx = route.stops.length - 1;
                      const lastStopName =
                        stops.find((s) => s.id === route.stops[lastIdx].stopId)?.name ||
                        t('routeBuilder.list.stopFallback', { number: lastIdx + 1 });
                      return (
                    <Accordion
                      disableGutters
                      elevation={0}
                      square
                      onClick={(e) => e.stopPropagation()}
                      onChange={(_e, expanded) =>
                        logEvent({
                          category: 'fixed_routes',
                          action: 'toggle_route_stops',
                          stop_count: route.stops.length,
                          expanded,
                        })
                      }
                      sx={{
                        mt: 1.5,
                        backgroundColor: 'transparent',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        component="div"
                        expandIcon={<Icon name="chevron-down" size={20} />}
                        sx={{
                          px: 0,
                          minHeight: 'auto',
                          '& .MuiAccordionSummary-content': { my: 0.5 },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {firstStopName}
                          </Typography>
                          <Icon name="chevron-right" size={16} style={{ color: '#9aa0a6' }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Icon name="location-dot" size={16} style={{ color: '#9aa0a6' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              {t('routeBuilder.list.stopsLabel', { count: route.stops.length })}
                            </Typography>
                          </Box>
                          <Icon name="chevron-right" size={16} style={{ color: '#9aa0a6' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {lastStopName}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0, pb: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      {route.stops.map((stopConfig, idx) => {
                        const stop = stops.find((s) => s.id === stopConfig.stopId);
                        const stopName = stop?.name || t('routeBuilder.list.stopFallback', { number: idx + 1 });
                        const waitTime = stopConfig.waitTimeMinutes;
                        
                        // Get travel time and distance TO the next stop (from current stop)
                        // For each stop, we show the travel time to the NEXT stop
                        const segment = idx < route.stops.length - 1
                          ? route.segments.find(
                              (seg) => seg.fromStopId === stopConfig.stopId && seg.toStopId === route.stops[idx + 1].stopId
                            )
                          : undefined;
                        // Apply traffic multiplier to travel times for display
                        const baseTravelTime = segment?.travelTimeMinutes;
                        const travelTime = baseTravelTime !== undefined 
                          ? Math.round(baseTravelTime * routeTrafficMultiplier)
                          : undefined;
                        const distance = segment?.distanceMeters;
                        
                        const isLast = idx === route.stops.length - 1;
                        
                        // For circulator routes, check if there's a return segment from last stop to first stop
                        const returnSegment = isCirculator && isLast && route.stops.length >= 2
                          ? route.segments.find(
                              (seg) => seg.fromStopId === stopConfig.stopId && seg.toStopId === route.stops[0].stopId
                            )
                          : undefined;
                        const baseReturnTravelTime = returnSegment?.travelTimeMinutes;
                        const returnTravelTime = baseReturnTravelTime !== undefined
                          ? Math.round(baseReturnTravelTime * routeTrafficMultiplier)
                          : undefined;
                        const returnDistance = returnSegment?.distanceMeters;
                        
                        return (
                          <Box key={stopConfig.stopId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {/* Stop Name */}
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 500,
                                minWidth: '140px',
                                fontSize: '0.95rem',
                              }}
                            >
                              {stopName}
                            </Typography>
                            
                            {/* Wait Time */}
                            {waitTime > 0 && (
                              <Chip
                                label={t('routeBuilder.list.waitChipLabel', { value: waitTime })}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '0.75rem',
                                  backgroundColor: 'primary.light',
                                  color: 'primary.contrastText',
                                }}
                              />
                            )}
                            
                            {/* Travel Time and Distance Arrow */}
                            {!isLast && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                                {travelTime !== undefined ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography 
                                      variant="body2" 
                                      color="text.secondary"
                                      sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                                    >
                                      {travelTime}min
                                    </Typography>
                                    {distance !== undefined && (
                                      <Typography 
                                        variant="body2" 
                                        color="text.disabled"
                                        sx={{ fontSize: '0.875rem' }}
                                      >
                                        ({formatDistance(distance, distanceUnit)})
                                      </Typography>
                                    )}
                                  </Box>
                                ) : segment && segment.travelTimeMinutes ? (
                                  // Segment exists but travelTime calculation failed - show raw value with multiplier
                                  <Typography 
                                    variant="body2" 
                                    color="text.secondary"
                                    sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                                  >
                                    {Math.round(segment.travelTimeMinutes * routeTrafficMultiplier)}min
                                  </Typography>
                                ) : (
                                  // No segment found - needs calculation
                                  <Typography
                                    variant="body2"
                                    color="warning.main"
                                    sx={{ fontSize: '0.875rem', fontStyle: 'italic' }}
                                  >
                                    {t('routeBuilder.list.calculateTime')}
                                  </Typography>
                                )}
                                <Icon name="chevron-right" size={20} style={{ 
                                    color: '#52565e',
                                    fontSize: '1.25rem',
                                  }} />
                              </Box>
                            )}
                            
                            {/* Return Segment for Circulator Routes */}
                            {isCirculator && isLast && returnTravelTime !== undefined && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography 
                                    variant="body2" 
                                    color="primary.main"
                                    sx={{ fontSize: '0.875rem', fontWeight: 600 }}
                                  >
                                    {returnTravelTime}min
                                  </Typography>
                                  {returnDistance !== undefined && (
                                    <Typography 
                                      variant="body2" 
                                      color="primary.main"
                                      sx={{ fontSize: '0.875rem', opacity: 0.8 }}
                                    >
                                      ({formatDistance(returnDistance, distanceUnit)})
                                    </Typography>
                                  )}
                                </Box>
                                <Icon name="repeat" size={20} style={{ 
                                    color: '#ef7335',
                                    fontSize: '1.25rem',
                                  }} />
                                <Typography 
                                  variant="body2" 
                                  color="primary.main"
                                  sx={{ fontSize: '0.875rem', fontWeight: 500 }}
                                >
                                  {stops.find((s) => s.id === route.stops[0].stopId)?.name || t('routeBuilder.list.startStopFallback')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                      );
                    })()}

                    {/* Vehicles & Operations — READ-ONLY view of the saved draft
                        deployment plan (route.operationAssignments). Composed in
                        the "Attach Vehicles & Operations" modal; shown here so the
                        plan is visible alongside the route. Renders nothing when no
                        assignments exist. Phase-2 deploy (writing shift solutions to
                        Ops + including the assignment in the route upload) hooks in
                        from the modal's Deploy button, not here. */}
                    {!!route.operationAssignments?.length && (
                      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                          <AltRouteOutlined fontSize="small" color="primary" />
                          <Typography variant="subtitle2" fontWeight="600">
                            {t('routeBuilder.list.vehiclesOperationsTitle', 'Vehicles & Operations')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                          {route.operationAssignments.map((assignment, opIdx) => (
                            <Box
                              key={`${assignment.operationSolutionId}-${opIdx}`}
                              sx={{
                                pl: 1.5,
                                borderLeft: '2px solid',
                                borderColor: 'divider',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <WorkOutlineOutlined fontSize="small" color="action" />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {assignment.operationName || assignment.operationSolutionId}
                                </Typography>
                                {assignment.dateRange && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <EventOutlined fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      {assignment.dateRange.startDate} → {assignment.dateRange.endDate}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                              {assignment.vehicleAssignments.map((vehicle, vIdx) => (
                                <Box
                                  key={`${vehicle.opsVehicleId}-${vIdx}`}
                                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pl: 1 }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <DirectionsBusOutlined fontSize="small" color="action" />
                                    <Typography variant="body2" color="text.secondary">
                                      {vehicle.opsVehicleLabel || vehicle.opsVehicleId}
                                    </Typography>
                                  </Box>
                                  {vehicle.serviceDays && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      <CalendarMonthOutlined fontSize="small" color="action" />
                                      <Typography variant="caption" color="text.secondary">
                                        {describeServiceDays(vehicle.serviceDays)}
                                      </Typography>
                                    </Box>
                                  )}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ScheduleIcon fontSize="small" color="action" />
                                    <Typography variant="caption" color="text.secondary">
                                      {vehicle.startTime}
                                      {vehicle.endTime ? ` – ${vehicle.endTime}` : ''}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={vehicle.direction}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20 }}
                                  />
                                  <Chip
                                    label={vehicle.shiftType}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20 }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            });
            })()}
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!routeToDelete} onClose={() => setRouteToDelete(null)}>
          <DialogTitle>{t('common.areYouSure')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('routeBuilder.deleteDialog.message', { name: routeToDelete?.name ?? '' })}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRouteToDelete(null)}>{t('common.cancel')}</Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained">
              {t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!orphanedStopsDecision} onClose={() => setOrphanedStopsDecision(null)}>
          <DialogTitle>{t('routeBuilder.orphanedDialog.title')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 1.5 }}>
              {t('routeBuilder.orphanedDialog.summary', { name: orphanedStopsDecision?.route.name ?? '', count: orphanedStopsDecision?.orphanStopIds.length ?? 0 })}
            </Typography>
            <Typography sx={{ mb: 1.5 }}>
              {t('routeBuilder.orphanedDialog.question')}
            </Typography>
            {!!orphanedStopsDecision && orphanedStopsDecision.orphanStopIds.length > 0 && (
              <Box sx={{ maxHeight: 180, overflowY: 'auto', pr: 0.5 }}>
                {orphanedStopsDecision.orphanStopIds.map((stopId) => {
                  const stopName = stops.find((stop) => stop.id === stopId)?.name || stopId;
                  return (
                    <Typography key={stopId} variant="body2" color="text.secondary">
                      • {stopName}
                    </Typography>
                  );
                })}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOrphanedStopsDecision(null)}>{t('common.cancel')}</Button>
            <Button
              onClick={() => {
                if (!orphanedStopsDecision) return;
                finalizeRouteDeletion(orphanedStopsDecision.route, orphanedStopsDecision.orphanStopIds, false);
              }}
            >
              {t('routeBuilder.orphanedDialog.keepStops')}
            </Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => {
                if (!orphanedStopsDecision) return;
                finalizeRouteDeletion(orphanedStopsDecision.route, orphanedStopsDecision.orphanStopIds, true);
              }}
            >
              {t('routeBuilder.orphanedDialog.deleteStops')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Recalculate from Stops Confirmation Dialog */}
        <Dialog open={showRecalculateDialog} onClose={() => setShowRecalculateDialog(false)}>
          <DialogTitle>{t('routeBuilder.recalculateDialog.title')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('routeBuilder.recalculateDialog.message')}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              {t('routeBuilder.recalculateDialog.warning')}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRecalculateDialog(false)}>{t('common.cancel')}</Button>
            <Button
              onClick={() => {
                if (selectedRoute) {
                  const updatedRoute = {
                    ...selectedRoute,
                    isManuallyEdited: false,
                    geometry: undefined, // Clear geometry to force recalculation
                  };
                  updateRoute(updatedRoute);
                  exitEditMode();
                }
                setShowRecalculateDialog(false);
              }}
              color="warning"
              variant="contained"
            >
              {t('routeBuilder.recalculateDialog.recalculate')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={handleRouteSummary}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="route" size={20} />
              <Typography>{t('routeBuilder.contextMenu.routeSummary')}</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleEditRoute}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="pen-to-square" size={20} />
              <Typography>{t('routeBuilder.contextMenu.editRoute')}</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={handleDuplicateRoute}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon name="clipboard" size={20} />
              <Typography>{t('routeBuilder.contextMenu.duplicateRoute')}</Typography>
            </Box>
          </MenuItem>
        </Menu>
        <RouteOverlapAnalysisModal />
      </CardContent>

        {optimizationModal}
      </Card>
      
      {/* Snackbar for save notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity="info" 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
